import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { getBeamCapacity, BEAM_MAX_CAPACITY, gridClassForCapacity } from "@/lib/beamCapacity";
import QuestsPanel from "@/components/QuestsPanel";
import BeamSafetyRules from "@/components/BeamSafetyRules";
import LiveEarningsPanel from "@/components/LiveEarningsPanel";
import LiveKitVideoTile from "@/components/LiveKitVideoTile";
import ProfilePreviewModal from "@/components/ProfilePreviewModal";
import LiveGiftOverlay from "@/components/LiveGiftOverlay";
import useLiveKitRoom from "@/hooks/useLiveKitRoom";
import SharedNavigation from "@/components/SharedNavigation";
import LiveRecordingControls from "@/components/LiveRecordingControls";
import { BeamRoleBadge, BeamKindBadge } from "@/components/live/BeamRoleBadge";
import { BeamLoadError } from "@/components/live/BeamLoadError";
import { BeamEndedPanel } from "@/components/live/BeamEndedPanel";
import { beamShareUrl } from "@/lib/shareBeam";
import ReplayPostEndModal from "@/components/ReplayPostEndModal";
import GiftDrawer from "@/components/live/GiftDrawer";
import ShareToCommunityDialog from "@/components/ShareToCommunityDialog";
import { BeamBoxLockToggle, BeamBoxEmptySlot } from "@/components/live/BeamBoxControls";
import BeamMusicDrawer from "@/components/live/BeamMusicDrawer";
import PrivateARPanel from "@/components/live/PrivateARPanel";
import PresentationStage from "@/components/live/PresentationStage";
import { applyMusicStateToAudio } from "@/lib/beamMusicReconcile";

/**
 * Phase 8 — 🎙️ Live Beams: single room view.
 *
 * Real-time WebSocket feed for presence, chat, and gift bursts. Sending
 * a gift hits POST /live/rooms/{id}/gift which broadcasts to every
 * client. Audio streaming itself is queued behind a LiveKit/Agora
 * carrier integration (banner says so explicitly).
 *
 * Mobile-first single-column layout; on >=md it becomes a 2-column
 * (left: speaker grid + chat, right: live gift feed + leaderboard).
 */

const wsUrlFor = (roomId, token) => {
  // Convert https://x → wss://x, http://x → ws://x
  const httpBase = BACKEND_URL || window.location.origin;
  const proto = httpBase.startsWith("https://") ? "wss://" : "ws://";
  const host = httpBase.replace(/^https?:\/\//, "");
  const q = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${proto}${host}/api/live/rooms/${roomId}/ws${q}`;
};

// Phase 21.1b — Gift picker now lives in the GiftDrawer bottom-sheet
// component (/components/live/GiftDrawer.jsx). The inline quick-picks
// and tabs that used to live here are no longer needed.

const LiveRoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [chat, setChat] = useState([]);
  const [recentGifts, setRecentGifts] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  // 🪑 Beam Boxes — multi-guest grid state, host controls, reactions
  const [reactionFloaters, setReactionFloaters] = useState([]);
  // 🎯 Quests — bumping this counter forces QuestsPanel to refetch after
  // a gift/reaction/join event. Kept inside its own state so QuestsPanel
  // re-renders alone — no parent remount of the WS or chat lists.
  const [questsRefreshTick, setQuestsRefreshTick] = useState(0);
  // 👤 Profile preview modal — opened on avatar tap anywhere in the room.
  const [previewUserId, setPreviewUserId] = useState(null);
  // 🙋 Raise Hand — local mirror of `me` being in the queue, even before
  // the next room poll arrives.
  const [handQueue, setHandQueue] = useState([]);
  const [bannedUserIds, setBannedUserIds] = useState([]);
  // 🎁 Live gift target selector (host-only). null = default-to-host
  // (which is meaningless when sender IS the host — host MUST pick).
  const [giftTargetId, setGiftTargetId] = useState(null);
  // Phase 9.7.6 — full live catalog. Used by the GiftDrawer (bottom-sheet).
  const [giftCatalog, setGiftCatalog] = useState([]);
  // Entry/exit toast throttle: max one toast per user per 10s.
  const presenceToastRef = useRef(new Map());
  // 🎁 Live gift overlay ref + spotlight glow per recipient.
  const giftOverlayRef = useRef(null);
  const [glowByUserId, setGlowByUserId] = useState({});
  // Phase 21.0 — Dynamic Beam Box ladder. Capacity unlocks from 5 → 8 → 12
  // → 16 → 20 as speakers join. We track the previous tier so we can show a
  // smooth "Room Expanded" banner exactly once per expansion (not on shrink).
  const [beamCapacityTier, setBeamCapacityTier] = useState(5);
  const [showExpandedBanner, setShowExpandedBanner] = useState(false);
  // Phase 3 V1 — Private A&R Listening Room. Bumped whenever an `ar_*`
  // WS event arrives so the PrivateARPanel can react without owning
  // its own socket. The panel is only mounted when
  // `room.room_type === "private_ar"`.
  const [arEvent, setArEvent] = useState(null);
  // Phase 21.0 — local analytics counters. Posted to backend on room exit
  // via the existing /api/lr/{room_id}/heartbeat or end-of-session hook.
  const beamAnalyticsRef = useRef({
    largest_beam_reached: 1,
    peak_concurrent_speakers: 1,
    speaker_count_samples: [],
    peak_queue_length: 0,
  });
  // Phase 14.3 — Bumped on every incoming `gift` WS event so the
  // LiveEarningsPanel can flash + float a "+coins" bubble.
  const [earningsPulse, setEarningsPulse] = useState(null);
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);

  const isHost = me?.user_id === room?.host_user_id;
  // Phase B — room-scoped moderator helper. Monitor assignments
  // flow in via the existing `monitor_promoted/demoted` WS events
  // that already live-update `room.monitor_user_ids` (see line 482).
  const isMonitor = !!me?.user_id && (room?.monitor_user_ids || []).includes(me.user_id);
  const canModerate = isHost || isMonitor;
  const boxesLocked = !!room?.boxes_locked;
  const meIsSpeaker = !!me?.user_id && (room?.guests || []).some((g) => g.user_id === me.user_id);
  const meIsBanned = !!me?.user_id && (room?.banned_user_ids || []).includes(me.user_id);
  const stageFull = (room?.guests?.length || 0) >= ((room?.max_guests || 19));

  // Phase B — Lock / Unlock Boxes (host or room-scoped monitor).
  const toggleBoxesLock = async () => {
    if (!canModerate) return;
    const path = boxesLocked ? "unlock" : "lock";
    try {
      await api.post(`/live/rooms/${roomId}/boxes/${path}`);
      toast.success(boxesLocked ? "🔓 Boxes unlocked" : "🔒 Boxes locked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update box lock");
    }
  };

  // Phase B — Participant self-join. Backend enforces Private A&R
  // authorization + lock + capacity + ban + duplicate guards.
  // Phase B mobile-UX — track the pending state so every open-slot
  // button on the stage flips to "⏳ Joining…" the instant the user
  // taps. The latch clears on success OR failure so a later retry
  // (different error, user came back after error) works unchanged.
  // M1.5 — Audio element is owned by LiveRoomPage (NOT by the
  // drawer) so the host can close the drawer and still have music
  // playing. The drawer is now pure UI; actual playback and all
  // API calls live here.
  const beamMusicAudioRef = useRef(null);
  const [beamMusicState, setBeamMusicState] = useState(null);
  const [beamMusicPending, setBeamMusicPending] = useState(false);

  // Lazily create the audio element on first use.
  const _getBeamMusicAudio = useCallback(() => {
    if (!beamMusicAudioRef.current && typeof window !== "undefined") {
      const el = new window.Audio();
      el.preload = "metadata";
      beamMusicAudioRef.current = el;
    }
    return beamMusicAudioRef.current;
  }, []);

  // Hydrate initial music_state from the room payload when it loads.
  useEffect(() => {
    const ms = room?.music_state || null;
    setBeamMusicState(ms);
  }, [room?.music_state]);

  // Release the room-scoped audio element when the user leaves Beam.
  useEffect(() => {
    return () => {
      if (beamMusicAudioRef.current) {
        try {
          beamMusicAudioRef.current.pause();
          beamMusicAudioRef.current.src = "";
        } catch (_e) { /* teardown — ignore */ }
        beamMusicAudioRef.current = null;
      }
    };
  }, []);

  const playBeamMusic = useCallback(async (submissionId) => {
    if (beamMusicPending || !roomId) return;
    setBeamMusicPending(true);
    try {
      const r = await api.post(
        `/live/rooms/${roomId}/music/play`,
        { submission_id: submissionId },
      );
      const ms = r.data?.music_state;
      setBeamMusicState(ms);
      const el = _getBeamMusicAudio();
      if (el && ms?.track_meta?.src_url) {
        const src = ms.track_meta.src_url.startsWith("http")
          ? ms.track_meta.src_url
          : `${BACKEND_URL}${ms.track_meta.src_url}`;
        // iOS Safari — pause + load before swapping src so a direct
        // track-switch (A → B without a manual Pause in between) also
        // works for the host. Mirrors the listener reconciler. Also
        // detaches any stale `loadedmetadata` handler left by a prior
        // deferred switch so it cannot resurrect the old track.
        try { el.pause(); } catch (_e) {}
        if (_pendingMetaHandlerRef.current) {
          try { el.removeEventListener("loadedmetadata", _pendingMetaHandlerRef.current); } catch (_e) {}
          _pendingMetaHandlerRef.current = null;
        }
        el.src = src;
        try { el.load(); } catch (_e) {}
        try { el.currentTime = ms.position_s_at_start || 0; } catch (_e) {}
        try { await el.play(); } catch (_e) { /* autoplay */ }
      }
      toast.success(`🎵 Playing ${ms?.track_meta?.title || "track"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not play track");
    } finally {
      setBeamMusicPending(false);
    }
  }, [beamMusicPending, roomId, _getBeamMusicAudio]);

  const pauseResumeBeamMusic = useCallback(async () => {
    if (beamMusicPending || !beamMusicState?.track_id) return;
    setBeamMusicPending(true);
    try {
      const el = _getBeamMusicAudio();
      if (beamMusicState.is_playing) {
        const r = await api.post(`/live/rooms/${roomId}/music/pause`);
        setBeamMusicState(r.data?.music_state);
        if (el) el.pause();
      } else {
        const r = await api.post(`/live/rooms/${roomId}/music/resume`);
        setBeamMusicState(r.data?.music_state);
        if (el) {
          el.currentTime = r.data?.music_state?.position_s_at_start || 0;
          try { await el.play(); } catch (_e) { /* autoplay */ }
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update playback");
    } finally {
      setBeamMusicPending(false);
    }
  }, [beamMusicPending, beamMusicState, roomId, _getBeamMusicAudio]);

  const clearBeamMusic = useCallback(async () => {
    if (beamMusicPending) return;
    setBeamMusicPending(true);
    try {
      await api.post(`/live/rooms/${roomId}/music/clear`);
      setBeamMusicState(null);
      const el = beamMusicAudioRef.current;
      if (el) {
        el.pause();
        el.src = "";
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear music");
    } finally {
      setBeamMusicPending(false);
    }
  }, [beamMusicPending, roomId]);

  // M2 — Synchronized room playback. These states + effects let every
  // listener in the room hear the same track at the same approximate
  // position. The server broadcasts `music_state_update` frames over
  // the Beam WebSocket; this file reconciles the HTMLAudioElement
  // against the latest authoritative state. Autoplay rejection on
  // iOS/Android produces an in-UI "Tap to Hear Music" prompt. LiveKit
  // active-speaker events duck the local music volume so a host's
  // voice stays intelligible.
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [listenerVolume, setListenerVolume] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("beam_music_vol") || "1");
      return isFinite(v) && v >= 0 && v <= 1 ? v : 1;
    } catch (_e) { return 1; }
  });
  const [duckingEnabled, setDuckingEnabled] = useState(() => {
    try {
      return localStorage.getItem("beam_music_ducking") !== "false";
    } catch (_e) { return true; }
  });
  const DUCK_VOLUME = 0.3;
  const DUCK_RAMP_MS = 180;
  // M2 clock-sync baseline. Captured on every WS music_state_update
  // frame AND on initial room load. Lets us compute the authoritative
  // playback position without trusting the device wall clock:
  //   expected_pos_s = position_s_at_start
  //                  + (is_playing ? ((performance.now() - monoAtReceive)/1000
  //                                  + (serverTimeAtReceive - startedAtServer)/1000)
  //                                : 0)
  // performance.now() is monotonic — it does NOT jump when the OS
  // adjusts the wall clock, when iOS wakes the tab from the lock
  // screen, or when NTP corrects the device clock mid-playback. This
  // isolates all skew into the one-time (server_time - started_at)
  // delta captured at frame receipt.
  const syncBaselineRef = useRef(null);
  // Ref holding the currently pending `loadedmetadata` handler so a
  // subsequent track-switch can detach it before it fires. Guards
  // against a slow metadata load from Track A restarting playback
  // after the user has already moved to Track B / C.
  const _pendingMetaHandlerRef = useRef(null);

  // Reconcile the audio element against the authoritative music_state.
  // Thin wrapper around the pure reconciler in `lib/beamMusicReconcile`
  // so the direct-switch + stale-handler guarantees stay unit-testable.
  // The reconciler itself owns pause-before-swap, load(), the drift
  // threshold, the clock-sync baseline math, and the autoplay-blocked
  // signalling. Everyone (host + listeners) runs it on every state
  // change — host-side imperative play remains, but is idempotent.
  const _applyMusicStateToAudio = useCallback((ms) => {
    const el = _getBeamMusicAudio();
    applyMusicStateToAudio(el, ms, {
      backendUrl: BACKEND_URL,
      baseline: syncBaselineRef.current,
      handlerRef: _pendingMetaHandlerRef,
      setAudioBlocked,
    });
  }, [_getBeamMusicAudio]);

  // Fire reconciliation whenever beamMusicState changes.
  useEffect(() => {
    _applyMusicStateToAudio(beamMusicState);
  }, [beamMusicState, _applyMusicStateToAudio]);

  // (The `visibilitychange` → loadRoom() useEffect was moved below
  // the `const loadRoom = useCallback(...)` declaration to avoid a
  // Temporal Dead Zone ReferenceError during the first render — the
  // deps array `[loadRoom]` is evaluated on every render, and
  // referring to a `const` before its declaration throws at mount.)

  // (The listener-volume + ducking-ramp useEffects that reference
  // `lkActiveSpeakerIds` were moved below the `useLiveKitRoom(...)`
  // destructuring block to avoid the same Temporal Dead Zone pattern.
  // See the matching block after the destructure for the actual
  // handlers.)

  // One-tap autoplay unblock. Fired by the "Tap to Hear Music" overlay
  // when iOS Safari / Chrome rejects audio.play() without a user
  // gesture. Replays the current state onto the same audio element.
  const tapToHearMusic = useCallback(async () => {
    const el = _getBeamMusicAudio();
    if (!el) return;
    try {
      await el.play();
      setAudioBlocked(false);
    } catch (_e) {
      // Still blocked — leave overlay visible for the user to retry.
    }
  }, [_getBeamMusicAudio]);

  const [joiningBox, setJoiningBox] = useState(false);
  // M1 — host music drawer open/close state.
  const [musicDrawerOpen, setMusicDrawerOpen] = useState(false);
  // Beam Music feature flag (Option A). Fetched from backend runtime
  // config so a build-time React env (which can't differ preview↔prod)
  // doesn't dictate visibility. Fail-closed: default OFF.
  const [beamMusicEnabled, setBeamMusicEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Backend returns {enabled, admin_only, accessible}. `accessible`
    // is the single-source-of-truth boolean for whether to render the
    // Music UI — enabled AND (not admin_only OR this user is admin).
    api.get("/config/beam-music")
      .then((r) => { if (!cancelled) setBeamMusicEnabled(!!r?.data?.accessible); })
      .catch(() => { if (!cancelled) setBeamMusicEnabled(false); });
    return () => { cancelled = true; };
  }, []);
  const joinBox = async () => {
    if (joiningBox) return; // guard against the user double-tapping
    setJoiningBox(true);
    try {
      await api.post(`/live/rooms/${roomId}/boxes/join`);
      toast.success("🪑 You joined the Beam Box");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "boxes_locked") toast.error("Boxes are locked");
      else if (detail === "Beam Box is full") toast.error("Stage is full");
      else if (detail === "not_authorized_for_private_ar") toast.error("Private A&R only — you're not on the invite list");
      else toast.error(detail || "Could not join the Beam Box");
    } finally {
      setJoiningBox(false);
    }
  };

  // 📹 LiveKit — connect to media SFU after we know who's authed.
  // Disabled for anon (no token), and disabled if room.status !== live.
  // CEO Jun 2026 — signed-out visitors may watch/listen (subscribe-only token).
  const livekitEnabled = room?.status === "live";
  const {
    state: lkState,
    role: lkRole,    canPublish: lkCanPublish,
    localParticipant: lkLocal,
    remoteParticipants: lkRemotes,
    toggleCamera: lkToggleCamera,
    toggleMic: lkToggleMic,
    switchCamera: lkSwitchCamera,
    cameraEnabled: lkCameraOn,
    micEnabled: lkMicOn,
    cameraDenied: lkCameraDenied,
    micDenied: lkMicDenied,
    refreshToken: lkRefreshToken,
    livekitConfigured,
    // Phase 21.1f — Live Media Recovery diagnostics + retry
    retry: lkRetry,
    failedReason: lkFailedReason,
    attempts: lkAttempts,
    // Phase 21.1g+ — Reconciliation trigger after LiveKit reconnect
    reconnectedAt: lkReconnectedAt,
    // iter_181 (P0) — Intentional-leave latch (call BEFORE Leave / End
    // Live so backend doesn't mark it as a failure).
    markIntentional: lkMarkIntentional,
    // M2 — stage participants currently speaking (ducking trigger).
    activeSpeakerIds: lkActiveSpeakerIds,
    audioBlocked: lkAudioBlocked,
    startAudio: lkStartAudio,
  } = useLiveKitRoom({ roomId, enabled: livekitEnabled });

  // M2 — Listener-volume + ducking effects. Placed AFTER the
  // `useLiveKitRoom(...)` destructure to avoid the Temporal Dead
  // Zone that would otherwise throw `Cannot access 'lkActiveSpeakerIds'
  // before initialization` during the first render (deps arrays are
  // evaluated eagerly and dereference the `const` before its binding
  // executes). Moving these here is behaviourally identical to the
  // original placement.
  useEffect(() => {
    try { localStorage.setItem("beam_music_vol", String(listenerVolume)); } catch (_e) {}
    const el = beamMusicAudioRef.current;
    if (el && !(lkActiveSpeakerIds || []).length) {
      try { el.volume = listenerVolume; } catch (_e) {}
    }
  }, [listenerVolume, lkActiveSpeakerIds]);
  useEffect(() => {
    try { localStorage.setItem("beam_music_ducking", duckingEnabled ? "true" : "false"); } catch (_e) {}
  }, [duckingEnabled]);

  // Ducking — smoothly ramp the audio element's volume based on
  // whether any LiveKit stage participant is currently speaking. Uses
  // a short setInterval so the volume glide is perceptible but
  // doesn't pump between words. When ducking is disabled, the volume
  // locks to the listener's chosen volume.
  useEffect(() => {
    const el = beamMusicAudioRef.current;
    if (!el) return undefined;
    const anySpeaking = duckingEnabled && (lkActiveSpeakerIds || []).length > 0;
    const target = anySpeaking ? DUCK_VOLUME : listenerVolume;
    const steps = 8;
    const stepMs = DUCK_RAMP_MS / steps;
    const from = (() => { try { return el.volume ?? 1; } catch (_e) { return 1; } })();
    let i = 0;
    const h = setInterval(() => {
      i += 1;
      const next = from + (target - from) * (i / steps);
      try { el.volume = Math.max(0, Math.min(1, next)); } catch (_e) {}
      if (i >= steps) clearInterval(h);
    }, stepMs);
    return () => clearInterval(h);
  }, [lkActiveSpeakerIds, duckingEnabled, listenerVolume]);

  // identity → LiveKit participant. Used to render <LiveKitVideoTile/>
  // inside the existing Beam Box grid without changing its structure.
  const lkParticipantByIdentity = useMemo(() => {
    const map = new Map();
    if (lkLocal?.identity) map.set(lkLocal.identity, lkLocal);
    for (const p of lkRemotes || []) {
      if (p?.identity) map.set(p.identity, p);
    }
    return map;
  }, [lkLocal, lkRemotes]);

  // Whenever we learn the host has just promoted us, re-fetch the LK
  // token so our publish grants kick in without leaving the page.
  const wasGuestRef = useRef(false);
  // Phase B stabilize — tracks the latest WS-authoritative updated_at
  // so a late REST snapshot cannot roll stage state back.
  const wsLatestUpdatedAt = useRef(null);
  // S2 (Feb 2026) — single-flight gate for the WS-onopen authoritative
  // resync so a reconnect burst doesn't spam concurrent loadRoom()
  // calls when the socket flaps.
  const wsOpenResyncInFlightRef = useRef(false);
  useEffect(() => {
    if (!me || !room) return;
    const isCurrentlyGuest = (room.guests || []).some(
      (g) => g.user_id === me.user_id,
    );
    if (isCurrentlyGuest && !wasGuestRef.current && lkRole === "listener") {
      lkRefreshToken().catch(() => {});
    }
    if (!isCurrentlyGuest && wasGuestRef.current && me.user_id !== room.host_user_id) {
      lkRefreshToken().catch(() => {});
    }
    wasGuestRef.current = isCurrentlyGuest;
  }, [room, me, lkRole, lkRefreshToken]);

  // Initial load
  // Phase 21.1g — Pull out the room load so it can be called on demand
  // (e.g., room_force_refresh socket event).
  const loadRoom = useCallback(async () => {
    try {
      const r = await api.get(`/live/rooms/${roomId}?recent_gifts_limit=10`);
      // Phase B stabilize — initial-load race guard. If a WS
      // beam_box_update has already arrived with a NEWER updated_at,
      // keep the WS-authoritative stage/queue state and only apply the
      // non-realtime fields from this REST snapshot.
      const wsAt = wsLatestUpdatedAt.current;
      const restAt = r.data?.room?.updated_at;
      if (wsAt && restAt && restAt < wsAt) {
        setRoom((prev) => prev ? ({
          ...r.data.room,
          guests: prev.guests,
          hand_queue: prev.hand_queue,
          muted_guests: prev.muted_guests,
          banned_user_ids: prev.banned_user_ids,
          pinned_guest_id: prev.pinned_guest_id,
          boxes_locked: prev.boxes_locked,
          monitor_user_ids: prev.monitor_user_ids,
          updated_at: prev.updated_at,
        }) : r.data.room);
      } else {
        setRoom(r.data.room);
      }
      setOnlineUsers(r.data.online_users || []);
      setRecentGifts((r.data.recent_gifts || []).slice().reverse());
      setHandQueue(r.data.room?.hand_queue || []);
      setBannedUserIds(r.data.room?.banned_user_ids || []);
      return r.data;
    } catch (e) {
      throw e;
    }
  }, [roomId]);

  // M2 — Visibility recovery. Placed AFTER `loadRoom` to avoid the
  // Temporal Dead Zone that would otherwise crash the first render.
  // When the tab becomes visible again (iOS waking from a locked
  // screen, Android resume), re-pull the authoritative room payload
  // so music_state reflects any play / pause / clear that happened
  // while we were backgrounded. Also nudges the audio element back
  // on if the OS paused it.
  useEffect(() => {
    const onVis = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState !== "visible") return;
      loadRoom().catch(() => {});
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVis);
      return () => document.removeEventListener("visibilitychange", onVis);
    }
    return undefined;
  }, [loadRoom]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await api.get("/auth/me");
        if (!cancelled) setMe(u.data);
      } catch (_e) { /* anonymous OK */ }
      try {
        await loadRoom();
        if (cancelled) return;
        setLoadError(null);
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.status === 404 ? "not_found" : "network");
      }
      try {
        const w = await api.get("/wallet/me");
        if (!cancelled) setWallet(w.data?.wallet);
      } catch (_e) { /* anon */ }
      try {
        const lb = await api.get(`/live/rooms/${roomId}/leaderboard?limit=10`);
        if (!cancelled) setLeaderboard(lb.data?.leaderboard || []);
      } catch (_e) { /* leaderboard unavailable */ }
      // Phase 9.7.6 — load full gift catalog so the live picker can show
      // every tier, not just 4 hardcoded picks.
      try {
        const cat = await api.get("/gifts/catalog");
        if (!cancelled) setGiftCatalog(cat.data?.gifts || []);
      } catch (_e) { /* catalog unavailable */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Extracted message handler so the reconnect loop can reuse it.
  // Kept as a stable ref-callback so it always sees latest state.
  // Hoist NOTE (P0 TDZ fix — Feb 2026): `handleWsMessageRef` and
  // `handleWsMessage` MUST be declared BEFORE the WebSocket useEffect
  // below, because that effect's dependency array references
  // `handleWsMessage`. Under strict `const` TDZ semantics the deps
  // array is evaluated eagerly on every render; if the declarations
  // live below the useEffect the minified bundle throws
  // `Cannot access 'Ne' before initialization` and the entire Live
  // Room screen crashes into the React error boundary. See
  // commit history for `TrackPage TDZ Hoist Fix` (same pattern).
  // The message-handler body itself is set later by a separate
  // useEffect that assigns to `handleWsMessageRef.current`.
  const handleWsMessageRef = useRef(null);
  const handleWsMessage = useCallback((msg) => {
    handleWsMessageRef.current?.(msg);
  }, []);

  // Open WS
  // Phase 21.4 Batch 4 — Auto-reconnect with exponential backoff +
  // pong-tracking heartbeat. On reconnect, call loadRoom() to catch
  // any events missed during the outage. If 2 consecutive heartbeats
  // fail (no pong), treat the socket as dead and force a reconnect.
  const wsReconnectStateRef = useRef({
    attempts: 0,
    closedByUnmount: false,
    toastShown: false,
  });
  useEffect(() => {
    if (!roomId) return;
    // Copy ref to a local so the cleanup below uses the same object
    // reference that was captured at effect setup (satisfies
    // react-hooks/exhaustive-deps ref-cleanup lint).
    const wsState = wsReconnectStateRef.current;
    const token = localStorage.getItem("session_token") || "";
    let heartbeatTimer = null;
    let pongCheckTimer = null;
    let reconnectTimer = null;
    let ws = null;
    let missedPongs = 0;
    let awaitingPong = false;
    wsState.closedByUnmount = false;

    const openSocket = () => {
      const isReconnect = wsReconnectStateRef.current.attempts > 0;
      try {
        ws = new WebSocket(wsUrlFor(roomId, token));
      } catch (_e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        wsReconnectStateRef.current.attempts = 0;
        wsReconnectStateRef.current.toastShown = false;
        missedPongs = 0;
        awaitingPong = false;
        // S2 (Feb 2026 regression fix) — authoritative resync on EVERY
        // successful open, not only reconnects. Closes the window where
        // a mutation lands between the initial REST load and the WS
        // attaching: in that window the WS wasn't open to receive the
        // beam_box_update, and the REST load already returned the
        // pre-mutation snapshot, so the client would never learn about
        // the change until a page refresh.
        //
        // In-flight dedup: the WS onopen can fire alongside the
        // foreground-resync useEffect (if the user tabs back during
        // reconnect). `wsOpenResyncInFlight` collapses close-together
        // WS-driven resyncs into one network call. The existing
        // `wsLatestUpdatedAt` guard inside loadRoom() keeps a stale
        // REST response from undoing a newer WS snapshot.
        if (!wsOpenResyncInFlightRef.current) {
          wsOpenResyncInFlightRef.current = true;
          loadRoom()
            .catch(() => { /* silent — next event will retry */ })
            .finally(() => { wsOpenResyncInFlightRef.current = false; });
        }
      };

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        // Phase 21.4 Batch 4 — treat ANY inbound frame as a liveness
        // signal, and explicitly clear the pending-pong flag on pong.
        awaitingPong = false;
        missedPongs = 0;
        if (msg.type === "pong") return;
        handleWsMessage(msg);
      };

      ws.onerror = () => { /* silent — surfaced via onclose */ };

      ws.onclose = () => {
        if (wsReconnectStateRef.current.closedByUnmount) return;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      wsReconnectStateRef.current.attempts += 1;
      const n = wsReconnectStateRef.current.attempts;
      // Exponential backoff: 0.5s, 1s, 2s, 4s, 8s (capped)
      const delay = Math.min(8000, 500 * Math.pow(2, n - 1));
      if (n >= 3 && !wsReconnectStateRef.current.toastShown) {
        wsReconnectStateRef.current.toastShown = true;
        toast("Reconnecting to live…", { duration: 2200 });
      }
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        if (wsReconnectStateRef.current.closedByUnmount) return;
        openSocket();
      }, delay);
    };

    // Best-effort join (auth-gated; anon will get 401 ignored)
    api.post(`/live/rooms/${roomId}/join`)
      .then(() => setQuestsRefreshTick((t) => t + 1))
      .catch(() => {});

    openSocket();

    // Heartbeat: send ping every 20s. Verify a pong (or any frame)
    // arrived within 6s; miss twice → treat WS as dead and reconnect.
    heartbeatTimer = setInterval(() => {
      try {
        if (ws?.readyState !== 1) return;
        awaitingPong = true;
        ws.send(JSON.stringify({ type: "ping" }));
        clearTimeout(pongCheckTimer);
        pongCheckTimer = setTimeout(() => {
          if (!awaitingPong) return;
          missedPongs += 1;
          awaitingPong = false;
          if (missedPongs >= 2) {
            // Dead socket — force close so onclose triggers reconnect.
            try { ws.close(); } catch (_e) { /* ignore */ }
          }
        }, 6000);
      } catch (_e) { /* ignore */ }
    }, 20000);

    return () => {
      wsState.closedByUnmount = true;
      clearInterval(heartbeatTimer);
      clearTimeout(pongCheckTimer);
      clearTimeout(reconnectTimer);
      try { ws?.close(); } catch (_e) {}
      api.post(`/live/rooms/${roomId}/leave`).catch(() => {});
    };
  }, [roomId, handleWsMessage, loadRoom]);

  // Hoist NOTE (P0 TDZ fix — Feb 2026): `handleWsMessageRef` and
  // `handleWsMessage` are declared ABOVE the WebSocket useEffect
  // (before the "// Open WS" block). Only the handler-body assignment
  // effect remains here — it can safely live below the WS effect
  // because it just writes to `.current`, no TDZ risk.
  useEffect(() => {
    handleWsMessageRef.current = (msg) => {
      if (msg.type === "music_state_update") {
        // M2 — authoritative music_state fan-out from the server.
        // Capture the monotonic sync baseline at the moment the frame
        // arrives so the reconciler can compute the correct seek
        // position without trusting the local wall clock. See the
        // syncBaselineRef comment for the formula.
        const serverTimeMs = Date.parse(msg.server_time || "") || Date.now();
        const monoAtReceive = typeof performance !== "undefined"
          ? performance.now()
          : Date.now();
        syncBaselineRef.current = {
          serverTimeAtReceive: serverTimeMs,
          monoAtReceive,
          ms: msg.music_state || null,
        };
        setBeamMusicState(msg.music_state || null);
        return;
      }
      if (msg.type === "presence_join" || msg.type === "presence_leave") {
        setRoom((prev) => prev ? { ...prev, online_count: msg.online_count } : prev);
        if (msg.type === "presence_join" && msg.user) {
          setOnlineUsers((prev) => {
            if (prev.find((u) => u.user_id === msg.user.user_id)) return prev;
            return [...prev, msg.user];
          });
          const uid = msg.user.user_id;
          if (uid && uid !== me?.user_id) {
            const last = presenceToastRef.current.get(uid) || 0;
            const now = Date.now();
            if (now - last > 10_000) {
              presenceToastRef.current.set(uid, now);
              toast(`🎉 @${msg.user.name || "Artist"} joined the live`, {
                duration: 2200,
                className: "live-entrance-toast",
              });
            }
          }
        } else if (msg.type === "presence_leave") {
          const leaving = msg.user_id;
          const leavingUser =
            onlineUsers.find((u) => u.user_id === leaving) || null;
          setOnlineUsers((prev) => prev.filter((u) => u.user_id !== leaving));
          if (leaving && leaving !== me?.user_id) {
            const last = presenceToastRef.current.get(`leave_${leaving}`) || 0;
            const now = Date.now();
            if (now - last > 10_000) {
              presenceToastRef.current.set(`leave_${leaving}`, now);
              toast(`👋 @${leavingUser?.name || "Listener"} left`, {
                duration: 2000,
              });
            }
          }
        }
      } else if (msg.type === "chat") {
        setChat((prev) => [...prev.slice(-100), {
          id: `${Date.now()}-${Math.random()}`,
          ...msg,
        }]);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      } else if (msg.type === "presentation_state_update") {
        // W1 (Feb 2026) — Workshop presentation state fan-out. Server
        // is authoritative; late joiners reconcile the same shape via
        // GET /live/rooms/{id}, so no catch-up REST call is needed.
        setRoom((prev) => prev ? {
          ...prev,
          presentation_state: msg.presentation_state || {
            slides: [], active_slide_id: null,
          },
        } : prev);
      } else if (msg.type === "gift") {
        const incoming = {
          gift_id: msg.gift?.id, emoji: msg.gift?.emoji, name: msg.gift?.name,
          coins: msg.gift?.coins, sender: msg.sender, message: msg.message,
          quantity: msg.quantity || msg.gift?.quantity || 1,
          unit_coins: msg.gift?.unit_coins || msg.gift?.coins,
          created_at: msg.created_at,
          transaction_id: msg.transaction_id || null,
          recipient_user_id: msg.recipient_user_id || null,
        };
        const dedupeKey = incoming.transaction_id
          || `${incoming.sender?.user_id || "anon"}_${incoming.gift_id || "?"}_${incoming.created_at || ""}`;
        setRecentGifts((prev) => {
          const filtered = prev.filter((g) => {
            const k = g.transaction_id
              || `${g.sender?.user_id || "anon"}_${g.gift_id || "?"}_${g.created_at || ""}`;
            return k !== dedupeKey;
          });
          return [...filtered.slice(-25), incoming];
        });
        setRoom((prev) => prev ? { ...prev, gift_total_coins: msg.room_gift_total_coins } : prev);
        giftOverlayRef.current?.enqueueGift(msg);
        setEarningsPulse({
          id: msg.transaction_id || `${Date.now()}_${Math.random()}`,
          coins: Number(msg.gift?.coins || 0),
          emoji: msg.gift?.emoji,
          tier: msg.gift?.tier,
          at: Date.now(),
        });
        const recipientId = msg.recipient_user_id;
        if (recipientId) {
          setGlowByUserId((prev) => ({ ...prev, [recipientId]: (prev[recipientId] || 0) + 1 }));
          setTimeout(() => {
            setGlowByUserId((prev) => {
              const next = { ...prev };
              const cur = (next[recipientId] || 1) - 1;
              if (cur <= 0) delete next[recipientId]; else next[recipientId] = cur;
              return next;
            });
          }, 3500);
        }
        if (recipientId && me?.user_id && recipientId === me.user_id
            && msg.sender?.user_id !== me.user_id) {
          api.get("/wallet/me")
            .then((w) => setWallet(w.data?.wallet || null))
            .catch(() => {});
          toast.success(
            `${msg.gift?.emoji || "🎁"} +${Number(msg.gift?.coins || 0).toLocaleString()}c from @${msg.sender?.artist_name || "Anon"}`,
            {
              description: msg.message
                ? `"${msg.message}"`
                : "Your earnings updated — keep it going",
              duration: 4500,
            },
          );
        }
        api.get(`/live/rooms/${roomId}/leaderboard?limit=10`)
          .then((r) => setLeaderboard(r.data?.leaderboard || []))
          .catch(() => {});
      } else if (msg.type === "room_force_refresh") {
        loadRoom().catch(() => { /* ignore */ });
      } else if (msg.type === "room_ended") {
        toast.info("This room has ended");
        setRoom((prev) => prev ? { ...prev, status: "ended" } : prev);
      } else if (msg.type === "room_updated") {
        setRoom((prev) => prev ? {
          ...prev,
          title: msg.title ?? prev.title,
          cover_image: msg.cover_image ?? prev.cover_image,
          topic: msg.topic ?? prev.topic,
          updated_at: msg.updated_at || prev.updated_at,
        } : prev);
        if (me?.user_id !== room?.host_user_id) {
          toast("✨ The host updated the live", { duration: 1800 });
        }
      } else if (msg.type === "recording_started") {
        setRoom((prev) => prev ? { ...prev, recording: {
          ...(prev.recording || {}),
          status: "starting",
          media_kind: msg.media_kind || (prev.recording?.media_kind || "audio"),
        }} : prev);
      } else if (msg.type === "recording_stopped") {
        setRoom((prev) => prev ? { ...prev, recording: {
          ...(prev.recording || {}),
          status: "stopping",
        }} : prev);
      } else if (msg.type === "monitor_promoted" || msg.type === "monitor_demoted") {
        setRoom((prev) => prev ? { ...prev,
          monitor_user_ids: msg.monitor_user_ids || prev.monitor_user_ids,
        } : prev);
        if (msg.type === "monitor_promoted" && msg.user_id === me?.user_id) {
          toast.success("🎛️ You're now a Monitor — help keep the room healthy");
        }
      } else if (msg.type === "boxes_locked" || msg.type === "boxes_unlocked") {
        // Phase B — realtime lock state. All clients update without refresh.
        setRoom((prev) => prev ? { ...prev,
          boxes_locked: !!msg.boxes_locked,
        } : prev);
      } else if (msg.type === "box_joined") {
        // Phase B — self-join broadcast. The follow-up beam_box_update
        // event (triggered by _broadcast_room_update on the same mutation)
        // carries the authoritative guests[] array, so we don't duplicate
        // state here — just surface a toast for the self-joiner.
        if (msg.user_id === me?.user_id) {
          toast.success("🪑 On stage");
          // Phase B stabilize — refresh LiveKit grants so can_publish
          // flips from false→true without waiting for a natural reconnect.
          (async () => {
            try {
              await lkRefreshToken();
              await new Promise((res) => setTimeout(res, 500));
              lkRetry();
            } catch (_e) {
              lkRetry();
            }
          })();
        }
      } else if (msg.type === "user_kicked") {
        if (msg.user_id === me?.user_id) {
          toast(`You were removed from this room`);
          navigate("/beams");
        }
      } else if (msg.type === "message_pinned" || msg.type === "message_unpinned") {
        setRoom((prev) => prev ? { ...prev,
          pinned_messages: msg.pinned_messages || [],
        } : prev);
      } else if (msg.type === "beam_box_update") {
        // S1 (Feb 2026 regression fix) — beam_box_update must land
        // even when the initial REST load hasn't resolved yet. The
        // previous `prev ? ({...}) : prev` reduce silently dropped
        // the authoritative stage snapshot in that window, which
        // produced the "host taps Join Box, artist doesn't appear
        // until page refresh" symptom.
        //
        // Rules:
        //   • Stale-order guard: a frame whose updated_at is older
        //     than the latest already-applied frame is ignored. The
        //     comparison uses ISO-8601 string ordering (lexicographic
        //     == chronological for the format the server emits).
        //   • Does NOT invent a "live" status. We only fill in the
        //     fields the WS actually carries; the follow-up REST load
        //     fills in title / cover / status / monitor_user_ids / etc.
        //   • Legitimate empty arrays and `false` values ARE honoured
        //     (Array.isArray(x) ? x : fallback — not `x || fallback`
        //     which would silently coerce `[]` into the fallback).
        //   • `wsLatestUpdatedAt` is bumped so a later REST load (or
        //     a stale WS retransmit) cannot overwrite the newer state.
        if (msg.updated_at
            && wsLatestUpdatedAt.current
            && String(msg.updated_at) < String(wsLatestUpdatedAt.current)) {
          return; // stale frame — newer snapshot already applied
        }
        if (msg.updated_at) {
          wsLatestUpdatedAt.current = msg.updated_at;
        }
        setRoom((prev) => {
          const base = prev || { room_id: roomId };
          return {
            ...base,
            guests: Array.isArray(msg.guests) ? msg.guests : (base.guests || []),
            max_guests: (typeof msg.max_guests === "number" && msg.max_guests > 0)
              ? msg.max_guests
              : base.max_guests,
            pinned_guest_id: msg.pinned_guest_id ?? null,
            muted_guests: Array.isArray(msg.muted_guests) ? msg.muted_guests : (base.muted_guests || []),
            hand_queue: Array.isArray(msg.hand_queue) ? msg.hand_queue : (base.hand_queue || []),
            banned_user_ids: Array.isArray(msg.banned_user_ids) ? msg.banned_user_ids : (base.banned_user_ids || []),
            updated_at: msg.updated_at || base.updated_at,
          };
        });
        if (Array.isArray(msg.hand_queue)) setHandQueue(msg.hand_queue);
        if (Array.isArray(msg.banned_user_ids)) setBannedUserIds(msg.banned_user_ids);
      } else if (msg.type === "hand_raised") {
        // Phase B stabilize — stop calling loadRoom(); the preceding
        // beam_box_update already carried authoritative hand_queue.
        if (me?.user_id === room?.host_user_id) {
          toast(`🙋 @${msg.user?.name || "Listener"} wants to come up`, {
            duration: 3500,
            description: `${msg.queue_length || 0} in queue`,
          });
        }
      } else if (msg.type === "hand_approved") {
        // Phase B stabilize — no REST reload; beam_box_update carries
        // the authoritative guests/queue snapshot.
        if (me?.user_id === msg.user_id) {
          toast.success("🎙️ You're up! You've been added to the Beam Box.");
          (async () => {
            try {
              await lkRefreshToken();
              await new Promise((res) => setTimeout(res, 500));
              lkRetry();
            } catch (_e) {
              lkRetry();
            }
          })();
        }
      } else if (msg.type === "hand_denied") {
        if (me?.user_id === msg.user_id) {
          toast.info("The host passed for now — you can raise your hand again later.");
        }
      } else if (msg.type === "kicked") {
        if (me?.user_id === msg.user_id) {
          toast.error("You've been removed from this Beam by the host.");
          setTimeout(() => navigate("/beams"), 1200);
        }
      } else if (msg.type === "presence_join" || msg.type === "presence_leave") {
        // Handled earlier — no-op fallthrough guard (do not remove above branch).
      } else if (msg.type === "guest_left_stage") {
        // Phase B stabilize — beam_box_update already fired with the
        // authoritative post-leave guests[]. No REST reload needed.
      } else if (msg.type === "reaction") {
        const id = `${Date.now()}-${Math.random()}`;
        setReactionFloaters((prev) => [...prev.slice(-12), {
          id, emoji: msg.emoji, name: msg.from?.name,
        }]);
        setTimeout(() => {
          setReactionFloaters((prev) => prev.filter((f) => f.id !== id));
        }, 2200);
      } else if (typeof msg.type === "string" && msg.type.startsWith("ar_")) {
        // Phase 3 V1 — Private A&R WS fan-in. Store the raw payload with
        // a monotonically-increasing seq so React re-fires the panel's
        // effect even for identical event types.
        setArEvent({ ...msg, _seq: Date.now() + Math.random() });
      }
    };
  }, [me, room, onlineUsers, roomId, loadRoom, navigate, lkRefreshToken, lkRetry]);

  // Phase 21.1i — LIVE STATE AUTHORITY:
  //   LiveKit = source of truth for participants, audio/video, beam slots.
  //   Backend = source of truth for METADATA only (title, cover, topic,
  //             gift totals, recording state, host info, room status).
  // The previous 25s "full loadRoom" overwrote LiveKit-derived state on
  // every tick, causing participants to briefly disappear. This sync now
  // MERGES metadata only — it never touches guests / online_users /
  // hand_queue / muted_guests / banned_user_ids / pinned_guest_id.
  useEffect(() => {
    if (!roomId) return;
    const mergeMetadata = async () => {
      try {
        const r = await api.get(`/live/rooms/${roomId}?recent_gifts_limit=10`);
        const next = r.data?.room;
        if (!next) return;
        setRoom((prev) => prev ? {
          // Keep ALL existing presence-derived state from prev:
          //   guests, online_count, hand_queue, muted_guests,
          //   banned_user_ids, pinned_guest_id, max_guests.
          ...prev,
          // Overwrite ONLY metadata fields the server is authoritative for:
          title: next.title ?? prev.title,
          cover_image: next.cover_image ?? prev.cover_image,
          topic: next.topic ?? prev.topic,
          host_name: next.host_name ?? prev.host_name,
          host_user_id: next.host_user_id ?? prev.host_user_id,
          status: next.status ?? prev.status,
          gift_total_coins: next.gift_total_coins ?? prev.gift_total_coins,
          recording: next.recording ?? prev.recording,
          monitor_user_ids: next.monitor_user_ids ?? prev.monitor_user_ids,
          pinned_messages: next.pinned_messages ?? prev.pinned_messages,
          updated_at: next.updated_at || prev.updated_at,
        } : next);
        // Recent gifts ARE persistence — server is authoritative here.
        if (Array.isArray(r.data?.recent_gifts)) {
          setRecentGifts(r.data.recent_gifts.slice().reverse());
        }
      } catch (_e) {
        // Silent — next tick will retry.
      }
    };
    const t = setInterval(mergeMetadata, 20_000);
    return () => clearInterval(t);
  }, [roomId]);

  // Phase 21.1g+ — After a LiveKit reconnect, re-pull authoritative
  // server state ONCE (this is a recovery moment, not a periodic poll).
  // We still call loadRoom() here because a fresh reconnect means our
  // local WS feed was disconnected and may have missed beam_box_update
  // events; the next live WS message will resume the LiveKit-driven feed.
  useEffect(() => {
    if (!lkReconnectedAt) return;
    loadRoom().catch(() => { /* silent */ });
  }, [lkReconnectedAt, loadRoom]);

  // Phase B iOS fix — Foreground authoritative resync.
  //
  // iOS Safari aggressively suspends background tabs. The underlying
  // TCP socket may stay `readyState === OPEN` while frames are silently
  // dropped on resume, so `ws.onclose` never fires and the heartbeat
  // can take ≥32 s (longer under timer throttling) to force a
  // reconnect. During that window every `beam_box_update` is missed
  // and `room.guests[]` goes stale — producing the CEO-reported
  // "artist present on Android, missing on iPhone, refresh fixes it"
  // symptom.
  //
  // Fix: on EACH foreground transition (visibilitychange→visible,
  // pageshow, focus), issue ONE authoritative `loadRoom()`. The
  // existing `wsLatestUpdatedAt` guard inside loadRoom() continues to
  // protect against overwriting newer WS stage state. No polling, no
  // recurring timer, no backend/LiveKit change.
  //
  // Mobile Safari may fire visibilitychange + pageshow + focus close
  // together. A tiny in-flight latch (closure-scoped `let`) collapses
  // that burst into one GET. The latch clears in `.finally`, so the
  // NEXT genuine foreground transition can resync again.
  useEffect(() => {
    if (!roomId) return;
    let inFlight = false;
    const resync = () => {
      if (inFlight) return;
      if (typeof document !== "undefined"
          && document.visibilityState === "hidden") return;
      inFlight = true;
      Promise.resolve()
        .then(() => loadRoom())
        .catch(() => { /* silent — next foreground will retry */ })
        .finally(() => { inFlight = false; });
    };
    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") resync();
    };
    const onPageShow = () => resync();
    const onFocus = () => resync();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("focus", onFocus);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("focus", onFocus);
      }
    };
  }, [roomId, loadRoom]);

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    if (!me) { toast.error("Sign in to chat"); return; }
    try {
      wsRef.current?.send(JSON.stringify({ type: "chat", text }));
      setChatInput("");
    } catch (_e) {
      toast.error("Connection lost — refresh");
    }
  };

  const sendGift = async (g, targetId = null, quantity = 1) => {
    if (!me) { navigate("/login"); return; }
    if (sending) return;
    const q = Math.max(1, Math.min(50, Number(quantity) || 1));
    const totalCost = g.coins * q;
    if (wallet && (wallet.balance || 0) < totalCost) {
      toast.error(q > 1
        ? `Not enough coins for ⚡ Power Boost ×${q} (${totalCost.toLocaleString()}c)`
        : "Not enough coins — top up at /wallet");
      return;
    }
    // Phase 9.6 Batch 2 — block self-gift on the client too so we never
    // surface a useless 400 from the server.
    if (targetId && targetId === me.user_id) {
      toast.error("You can't gift yourself");
      return;
    }
    setSending(true);
    try {
      const payload = { gift_id: g.id, quantity: q };
      if (targetId) payload.target_user_id = targetId;
      const r = await api.post(`/live/rooms/${roomId}/gift`, payload);
      setWallet(r.data?.wallet || wallet);
      const s = r.data?.streak;
      if (s?.newly_unlocked_badge) {
        toast.success(`${s.newly_unlocked_badge.emoji} ${s.newly_unlocked_badge.label} unlocked!`);
      }
      if (q > 1) {
        toast.success(`⚡ Power Boost — sent ${q}× ${g.emoji} ${g.name}`);
      }
      // Phase 21.4 Batch 4 — Gift animation fallback for the sender.
      // Enqueue immediately after a successful send so the sender ALWAYS
      // sees the animation, even if the WS broadcast is delayed / dropped
      // on a flaky connection. The transaction_id dedupe on the overlay +
      // recentGifts prevents a double-render when the WS broadcast lands.
      try {
        const txn = r.data?.transaction || {};
        giftOverlayRef.current?.enqueueGift({
          type: "gift",
          transaction_id: txn.transaction_id,
          sender: {
            user_id: me.user_id,
            artist_name: me.artist_name || me.name,
            profile_photo: me.profile_photo || me.picture,
          },
          recipient_user_id: targetId || null,
          gift: {
            id: g.id,
            emoji: g.emoji,
            name: g.name,
            coins: (g.coins || 0) * q,
            unit_coins: g.coins || 0,
            quantity: q,
            tier: g.tier,
            animation: g.animation || "float",
            category: g.category || "core",
          },
          quantity: q,
          message: payload.message || null,
          created_at: txn.created_at || new Date().toISOString(),
        });
      } catch (_e) { /* overlay unavailable — silent */ }
      // 🎯 Quests — refresh after a successful live gift.
      setQuestsRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send gift");
    } finally {
      setSending(false);
    }
  };

  // 🚪 Leave Stage (guest self-leave from speaker slot). Listener "Leave
  // Beam" is just navigate('/beams') — WS auto-disconnects on unmount.
  const leaveStage = async () => {
    if (!me) return;
    // iter_181 (P0) — Flag the impending LiveKit disconnect so the
    // client-event beacon logs it as intentional, not a failure.
    try { lkMarkIntentional && lkMarkIntentional("intentional"); } catch (_e) { /* ignore */ }
    try {
      const r = await api.post(`/live/rooms/${roomId}/guests/leave`);
      if (r.data?.left) toast("👋 You've stepped down from the stage");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't leave stage");
    } finally {
      loadRoom().catch(() => {});
    }
  };
  const leaveBeam = () => {
    // iter_181 (P0) — Listener clicked "Leave Beam". Mark intentional
    // before the WS/LK teardown fires on unmount.
    try { lkMarkIntentional && lkMarkIntentional("intentional"); } catch (_e) { /* ignore */ }
    navigate("/beams");
  };

  // 🚪 Back button behaviour:
  //  - Listener / guest:  go straight to /home
  //  - Host with live stream: confirm via prompt — leave, end, or cancel
  const [showHostLeavePrompt, setShowHostLeavePrompt] = useState(false);

  // Phase 21.1a — Host "Edit Live" modal (title + cover image).
  const [showEditLive, setShowEditLive] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editCover, setEditCover] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  const openEditLive = () => {
    setEditTitle(room?.title || "");
    setEditCover(room?.cover_image || "");
    setShowEditLive(true);
  };

  const uploadCoverFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB)");
      return;
    }
    setEditUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "live_cover");
      const r = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("Upload failed");
      setEditCover(url);
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload cover");
    } finally {
      setEditUploading(false);
    }
  };

  const saveLiveEdits = async () => {
    const title = (editTitle || "").trim();
    if (!title) { toast.error("Title can't be empty"); return; }
    if (title.length > 80) { toast.error("Title is too long (max 80)"); return; }
    setEditSaving(true);
    try {
      const r = await api.patch(`/live/rooms/${roomId}`, {
        title,
        cover_image: editCover || "",
      });
      // Optimistically reflect — the WS broadcast will also arrive shortly.
      setRoom((prev) => prev ? {
        ...prev,
        title: r.data?.room?.title ?? title,
        cover_image: r.data?.room?.cover_image ?? (editCover || null),
      } : prev);
      toast.success("✨ Live updated");
      setShowEditLive(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update live");
    } finally {
      setEditSaving(false);
    }
  };

  const goBack = () => {
    if (isHost && isLive) {
      setShowHostLeavePrompt(true);
    } else {
      navigate("/home");
    }
  };

  const endRoom = async (confirmed = false) => {
    if (confirmed !== true && !window.confirm("End this Live Beam for everyone? Listeners will be moved to the replay page.")) return;
    // iter_181 (P0) — Host clicked End Live. For the host we mark
    // intentional so their own LiveKit disconnect doesn't count as a
    // failure. Other participants will receive LiveKit ROOM_DELETED /
    // ROOM_CLOSED, which useLiveKitRoom.js maps to server_ended (non-
    // intentional but not a network failure) automatically.
    try { lkMarkIntentional && lkMarkIntentional("intentional"); } catch (_e) { /* ignore */ }
    try {
      await api.post(`/live/rooms/${roomId}/end`);
      toast.success("Live Beam ended");
      // Phase 9.7 Batch A — if we were recording, give the host a chance
      // to Save/Discard before sending them home.
      if (room?.recording?.egress_id) {
        setShowReplayModal(true);
        return;
      }
      setRoom((prev) => prev ? { ...prev, status: "ended" } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to end");
    }
  };

  // Phase 9.7 Batch A — 🎬 host recording toggles.
  const [recBusy, setRecBusy] = useState(false);
  const [showReplayModal, setShowReplayModal] = useState(false);
  // Phase 9.7.2 — track availability of recording service so we hide the
  // REC button entirely if the backend reports no LiveKit/Cloudinary config.
  const [recAvailable, setRecAvailable] = useState(true);

  // Probe recording-quota once on mount; if the backend returns 503-ish
  // signal that recording isn't configured, hide the host button.
  useEffect(() => {
    if (!isHost) return;
    (async () => {
      try {
        const r = await api.get("/me/recording-quota");
        if (r.data?.service_available === false) setRecAvailable(false);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 503) setRecAvailable(false);
      }
    })();
  }, [isHost]);

  const startRecording = async () => {
    setRecBusy(true);
    try {
      const videoEnabled = lkState === "connected" && lkCameraOn;
      const r = await api.post(`/live/rooms/${roomId}/recording/start`, {
        video_enabled: !!videoEnabled,
      });
      const replay = r.data?.replay;
      setRoom((prev) => prev ? { ...prev, recording: {
        egress_id: replay.egress_id, replay_id: replay.replay_id,
        status: replay.egress_status, started_at: replay.started_at,
        media_kind: replay.media_kind,
      }} : prev);
      toast.success("🔴 Recording started");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 503) {
        setRecAvailable(false);
        toast.error("Recording service is temporarily unavailable. Try again later.");
      } else if (status === 429) {
        toast.error(detail || "You've used up your monthly recording quota");
      } else if (status === 403) {
        toast.error("Only the host can record this room");
      } else {
        toast.error(detail || "Couldn't start recording — please retry in a moment");
      }
    } finally {
      setRecBusy(false);
    }
  };
  const stopRecording = async () => {
    setRecBusy(true);
    try {
      await api.post(`/live/rooms/${roomId}/recording/stop`);
      setRoom((prev) => prev?.recording ? { ...prev, recording: {
        ...prev.recording, status: "stopping",
      }} : prev);
      toast.success("⏹ Stopping recording…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't stop");
    } finally {
      setRecBusy(false);
    }
  };

  // 🔄 Front/back camera switch — mobile-first.
  const switchCamera = async () => {
    if (!lkCanPublish || !lkCameraOn) {
      toast.info("Turn camera on first to switch");
      return;
    }
    const res = await lkSwitchCamera();
    if (res?.ok && res?.matched) {
      toast.success("📷 Camera switched");
    } else if (res?.reason === "exact_retry_failed" || res?.matched === false) {
      // Phase 21.4 Batch 5 — Clear retry state instead of silently
      // staying on the wrong camera.
      toast.error("Camera switch didn't stick — tap again to retry", {
        description: `Wanted ${res?.requested || "other"}, still on ${res?.actual || "current"}`,
      });
    } else {
      toast.info("Only one camera detected on this device");
    }
  };

  // 📤 Share live beam — Phase 21.4 Batch 5:
  //   • Native share and clipboard fallback MUST include title + URL.
  //   • Cover is ONLY attached as a preview file — the raw Cloudinary
  //     URL is NEVER printed into the message text.
  //   • "Share to RGM Community" is offered as a follow-up action.
  const shareRoom = async () => {
    const url = beamShareUrl(roomId);
    const title = room?.title || "Join my Beam";
    const cover = room?.cover_image || null;
    const hostLine = room?.host_name ? `Host: ${room.host_name}` : null;

    // Compose: title + host + URL only. NO raw cover URL in the body.
    const composeLines = [
      `🎙 ${title}`,
      hostLine,
      `Watch live: ${url}`,
    ].filter(Boolean);
    const compose = composeLines.join("\n");

    if (navigator.share) {
      try {
        const sharePayload = { title, text: compose, url };
        if (cover && navigator.canShare) {
          try {
            const resp = await fetch(cover, { mode: "cors" });
            if (resp.ok) {
              const blob = await resp.blob();
              const file = new File([blob], "live-cover.jpg", { type: blob.type });
              // Attach the file ONLY when the target accepts the full
              // {title, text, url, files} payload — never files-only.
              if (navigator.canShare({ title, text: compose, url, files: [file] })) {
                sharePayload.files = [file];
              }
            }
          } catch (_e) { /* cover fetch failed — share without file */ }
        }
        await navigator.share(sharePayload);
        return;
      } catch (_e) { /* user cancelled — fall through to clipboard */ }
    }
    // Clipboard fallback: title + host + URL. NO cover URL in text.
    try {
      await navigator.clipboard.writeText(compose);
      toast.success("🔗 Live details copied — paste anywhere", {
        description: `${title} · ${url}`,
      });
    } catch (_e) {
      toast.error("Couldn't copy link");
    }
  };

  // 📣 Share to RGM Community — reuses existing ShareToCommunityDialog +
  // POST /crews/{id}/posts flow. No new backend endpoint.
  const [showCommunityShare, setShowCommunityShare] = useState(false);

  // Phase 21.1b — Gift Drawer (bottom-sheet) open state. Replaces the
  // always-visible inline gift Card so the Beam grid + live video are
  // never obscured.
  const [giftDrawerOpen, setGiftDrawerOpen] = useState(false);

  const isLive = room?.status === "live";

  const sendReaction = (emoji) => {
    try { wsRef.current?.send(JSON.stringify({ type: "reaction", emoji })); }
    catch (_e) {}
    // 🎯 Quests — refresh after a reaction (debounced inside QuestsPanel).
    setQuestsRefreshTick((t) => t + 1);
  };

  const promoteListener = async (userId) => {
    try {
      await api.post(`/live/rooms/${roomId}/guests/invite`, { user_id: userId });
      toast.success("Invited to the Beam Box");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't invite");
    } finally {
      loadRoom().catch(() => {});
    }
  };
  const removeGuest = async (userId) => {
    try { await api.post(`/live/rooms/${roomId}/guests/remove`, { user_id: userId }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { loadRoom().catch(() => {}); }
  };
  const toggleMute = async (userId, isMuted) => {
    try {
      await api.post(`/live/rooms/${roomId}/guests/${isMuted ? "unmute" : "mute"}`, { user_id: userId });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { loadRoom().catch(() => {}); }
  };
  const togglePin = async (userId, isPinned) => {
    try {
      await api.post(`/live/rooms/${roomId}/guests/pin`, { user_id: isPinned ? null : userId });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { loadRoom().catch(() => {}); }
  };
  // 🚫 Block a user from the room (kick + ban).
  const blockUser = async (userId) => {
    if (!window.confirm("Block this user from the room? They'll be removed and can't rejoin.")) return;
    try {
      await api.post(`/live/rooms/${roomId}/guests/block`, { user_id: userId });
      toast.success("User blocked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to block"); }
    finally { loadRoom().catch(() => {}); }
  };
  // 🙋 Listener Raise Hand controls.
  const myHandRaised = !!handQueue.find((q) => q.user_id === me?.user_id);
  const raiseHand = async () => {
    if (!me) { navigate("/login"); return; }
    try {
      await api.post(`/live/rooms/${roomId}/hand/raise`);
      toast.success("🙋 Hand raised — host will be notified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't raise hand"); }
    finally { loadRoom().catch(() => {}); }
  };
  const lowerHand = async () => {
    try {
      await api.post(`/live/rooms/${roomId}/hand/lower`);
      toast("Hand lowered");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { loadRoom().catch(() => {}); }
  };
  const approveHand = async (userId) => {
    // Phase 21.4 Batch 5 — Backend is now idempotent. Reconcile client
    // state from the response instead of trusting the stale queue array.
    try {
      const r = await api.post(`/live/rooms/${roomId}/hand/approve`, { user_id: userId });
      const reason = r.data?.reason;
      if (reason === "already_speaker") {
        toast("Already on the Beam — cleaned up queue");
      } else if (reason === "already_processed") {
        toast("Already processed — reconciling");
      } else {
        toast.success("Approved — they're on the Beam");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve");
    } finally {
      loadRoom().catch(() => {});
    }
  };
  const denyHand = async (userId) => {
    try {
      await api.post(`/live/rooms/${roomId}/hand/deny`, { user_id: userId });
      toast("Request dismissed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      loadRoom().catch(() => {});
    }
  };

  // 🪑 Beam Box "speakers" = host + invited guests. Pinned slot first.
  const beamBoxSpeakers = useMemo(() => {
    if (!room) return [];
    const hostSpeaker = {
      user_id: room.host_user_id,
      name: room.host_name,
      profile_photo: room.host_profile_photo,
      is_host: true,
    };
    const guests = (room.guests || []).map((g) => ({
      user_id: g.user_id, name: g.name, profile_photo: g.profile_photo,
      is_host: false,
    }));
    let speakers = [hostSpeaker, ...guests];
    const pinId = room.pinned_guest_id;
    if (pinId) {
      const pinned = speakers.find((s) => s.user_id === pinId);
      if (pinned) {
        speakers = [pinned, ...speakers.filter((s) => s.user_id !== pinId)];
      }
    }
    return speakers.slice(0, 20);  // Phase 21.0 — hard ceiling at 20; the
    // active capacity tier (rendered as grid) is computed separately so the
    // UI smoothly unlocks 5 → 8 → 12 → 16 → 20 without dropping speakers.
  }, [room]);

  // Phase 21.0 — Dynamic Beam Box capacity ladder. Returns the slot count
  // for the current speaker count: 5 / 8 / 12 / 16 / 20. Sourced from the
  // single-source-of-truth utility (covered by 12 unit tests).
  const beamCapacity = useMemo(
    () => getBeamCapacity(beamBoxSpeakers.length),
    [beamBoxSpeakers.length],
  );

  // Detect tier expansions to flash the "Room Expanded" banner. Only fires
  // on growth — shrinks (someone leaves) are silent.
  useEffect(() => {
    if (beamCapacity > beamCapacityTier) {
      setBeamCapacityTier(beamCapacity);
      setShowExpandedBanner(true);
      const t = setTimeout(() => setShowExpandedBanner(false), 4500);
      return () => clearTimeout(t);
    } else if (beamCapacity < beamCapacityTier) {
      // Quietly collapse the tier to track future expansions accurately.
      setBeamCapacityTier(beamCapacity);
    }
  }, [beamCapacity, beamCapacityTier]);

  // Sample analytics on every speaker / queue change. Cheap — just refs.
  useEffect(() => {
    const stats = beamAnalyticsRef.current;
    const n = beamBoxSpeakers.length;
    stats.largest_beam_reached = Math.max(stats.largest_beam_reached, n);
    stats.peak_concurrent_speakers = Math.max(stats.peak_concurrent_speakers, n);
    stats.speaker_count_samples.push(n);
    if (stats.speaker_count_samples.length > 500) stats.speaker_count_samples.shift();
    stats.peak_queue_length = Math.max(stats.peak_queue_length, (handQueue || []).length);
  }, [beamBoxSpeakers.length, handQueue]);

  // Phase 21.0 — Automatic queue promotion (host-only).
  // The instant the room has an open slot AND the hand queue has someone
  // waiting, the host's client auto-fires the existing promote endpoint
  // for the next user in queue. Eliminates "no host action required" for
  // the common case while preserving the host's ability to skip/block.
  const autoPromoteInFlightRef = useRef(new Set());
  const [autoAdmit, setAutoAdmit] = useState(() => { try { return localStorage.getItem("beam_auto_admit") === "1"; } catch (_e) { return false; } });
  useEffect(() => { try { localStorage.setItem("beam_auto_admit", autoAdmit ? "1" : "0"); } catch (_e) { /* ignore */ } }, [autoAdmit]);
  useEffect(() => {
    if (!isHost || !roomId || !autoAdmit) return;
    if (beamBoxSpeakers.length >= beamCapacity) return;
    if (!handQueue || handQueue.length === 0) return;
    const nextUser = handQueue[0];
    if (!nextUser?.user_id) return;
    // Skip if we're already in the middle of promoting this user.
    if (autoPromoteInFlightRef.current.has(nextUser.user_id)) return;
    // Skip if they're already a speaker (race condition).
    if (beamBoxSpeakers.some((s) => s.user_id === nextUser.user_id)) return;
    autoPromoteInFlightRef.current.add(nextUser.user_id);
    const t = setTimeout(async () => {
      try {
        await api.post(`/live/rooms/${roomId}/guests/invite`, { user_id: nextUser.user_id });
        toast.success(`🪑 Auto-promoted ${nextUser.name || "next artist"} from queue`);
      } catch (_e) {
        // Silent — host can still promote manually if backend rejects.
        return;
      } finally {
        // Phase 21.4 Batch 5 — always reconcile from backend after an
        // auto-promote so the host tile grid tracks server truth.
        loadRoom().catch(() => {});
        // Release the lock a beat later so the next room state update can fire.
        setTimeout(() => autoPromoteInFlightRef.current.delete(nextUser.user_id), 1500);
      }
    }, 800);  // debounce — gives leaving speaker's state time to settle
    return () => clearTimeout(t);
  }, [isHost, roomId, autoAdmit, beamCapacity, beamBoxSpeakers, handQueue, loadRoom]);

  const mutedSet = useMemo(
    () => new Set(room?.muted_guests || []),
    [room]
  );

  // Phase 21.4 Batch 6 → Batch 8 — Stuck "Joining…" reconciler. If a
  // guest is in backend.guests[] but their LiveKit participant hasn't
  // attached within ~20s, force a room refetch. AND if the stuck user
  // is ME (the local viewer), also refresh the LK token + retry the
  // connection — that's the most common cause on iOS Safari after a
  // just-promoted user's token still has can_publish=false.
  const joiningTimersRef = useRef(new Map());
  const selfJoiningTriedRef = useRef(false);
  useEffect(() => {
    if (!room) return;
    const currentIds = new Set();
    const now = Date.now();
    // Track self's joining state too (batch8): as a promoted listener
    // whose LK track hasn't published yet.
    const meId = me?.user_id;
    const meIsGuest = meId && (room.guests || []).some(
      (g) => g?.user_id === meId,
    );
    const meHasLk = meId && lkParticipantByIdentity?.get?.(meId);
    if (meIsGuest && !meHasLk && meId !== room.host_user_id) {
      currentIds.add(meId);
      if (!joiningTimersRef.current.has(meId)) {
        joiningTimersRef.current.set(meId, now);
      }
    } else if (meHasLk) {
      // Clear self-flag so a future promotion re-arms the LK refresh.
      selfJoiningTriedRef.current = false;
      joiningTimersRef.current.delete(meId);
    }
    for (const g of (room.guests || [])) {
      const uid = g?.user_id;
      if (!uid || uid === room.host_user_id) continue;
      if (uid === meId) continue;
      const hasLk = lkParticipantByIdentity?.get?.(uid);
      if (hasLk) {
        joiningTimersRef.current.delete(uid);
        continue;
      }
      currentIds.add(uid);
      if (!joiningTimersRef.current.has(uid)) {
        joiningTimersRef.current.set(uid, now);
      }
    }
    // Clean timers for users no longer in guests.
    for (const key of Array.from(joiningTimersRef.current.keys())) {
      if (!currentIds.has(key)) joiningTimersRef.current.delete(key);
    }
    // Check every 4s whether any tile has been stuck > 20s.
    const timer = setInterval(() => {
      const n = Date.now();
      let anyStuck = false;
      let selfStuck = false;
      for (const [uid, since] of joiningTimersRef.current.entries()) {
        if (n - since > 20_000) {
          anyStuck = true;
          if (uid === meId) selfStuck = true;
          // eslint-disable-next-line no-console
          console.warn(`[beam] Stuck 'Joining…' > 20s for user=${uid} — reconciling`);
          joiningTimersRef.current.delete(uid);
        }
      }
      if (anyStuck) loadRoom().catch(() => {});
      // Phase 21.4 Batch 8 — Self-heal: if the stuck user is ME, my
      // LK token probably still has can_publish=false from before the
      // promotion. Refresh the token and retry LK connection.
      if (selfStuck && !selfJoiningTriedRef.current) {
        selfJoiningTriedRef.current = true;
        (async () => {
          try {
            toast("🔄 Refreshing your live connection…", { duration: 2000 });
            await lkRefreshToken();
            await new Promise((res) => setTimeout(res, 300));
            lkRetry();
          } catch (_e) { /* silent */ }
        })();
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [room, me, lkParticipantByIdentity, loadRoom, lkRefreshToken, lkRetry]);

  // Phase 21.1b — Recipient candidates for the Gift Drawer. Lifted to
  // component scope so the FAB trigger and the drawer share the same
  // memoized list (was previously inlined inside the JSX, recreated each
  // render).
  const giftRecipientCandidates = useMemo(() => {
    if (!room) return [];
    const list = [];
    if (!isHost && room.host_user_id) {
      list.push({
        user_id: room.host_user_id,
        name: (room.host_name || "Host") + " (Host)",
      });
    }
    (room.guests || []).forEach((g) => {
      if (g.user_id !== me?.user_id && !list.find((c) => c.user_id === g.user_id)) {
        list.push({ user_id: g.user_id, name: `${g.name} (Guest)` });
      }
    });
    onlineUsers.forEach((u) => {
      if (u.user_id !== me?.user_id && !list.find((c) => c.user_id === u.user_id)) {
        list.push({ user_id: u.user_id, name: u.name });
      }
    });
    return list;
  }, [room, isHost, me, onlineUsers]);

  const effectiveGiftTarget = giftTargetId
    || (isHost ? (giftRecipientCandidates[0]?.user_id || null) : (room?.host_user_id || null));

  // Listeners NOT currently a speaker — host can promote them.
  const promotableListeners = useMemo(() => {
    if (!room) return [];
    const speakerIds = new Set(beamBoxSpeakers.map((s) => s.user_id));
    return onlineUsers.filter(
      (u) => !speakerIds.has(u.user_id) && u.user_id !== room.host_user_id,
    );
  }, [onlineUsers, beamBoxSpeakers, room]);

  if (!room && loadError) {
    return <BeamLoadError kind={loadError} onRetry={() => { setLoadError(null); loadRoom().catch((e) => setLoadError(e?.response?.status === 404 ? "not_found" : "network")); }} />;
  }
  if (room && !isLive && !showReplayModal) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
        <SharedNavigation currentUser={me} activePage="beams" />
        <BeamEndedPanel room={room} isHost={isHost} />
      </div>
    );
  }
  if (!room) {
    return (
      <div className="min-h-screen bg-[#08060f] text-white flex items-center justify-center">
        <p className="text-pink-200/70 text-sm" data-testid="live-room-loading">Loading room…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      {/* 🚪 Floating Back Button — fixed-positioned at the viewport
          top-left so no parent overflow/transform can hide it.
          Renders for every role (host / guest / listener). */}
      <button
        type="button"
        onClick={goBack}
        className="fixed top-3 left-3 sm:top-4 sm:left-4 z-[60] flex items-center gap-1.5 text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 active:scale-95 border-2 border-white/30 rounded-full px-4 py-2 shadow-xl shadow-pink-500/40 transition-all"
        data-testid="live-room-back-btn"
        aria-label="Back to Home"
      >
        <span aria-hidden className="text-base">←</span>
        <span>Back</span>
      </button>

      <div className="max-w-6xl mx-auto px-4 py-3 sm:py-5" data-testid="live-room-page">
        {/* Header — keeps Share + End buttons accessible at the top. The
            Back button above is fixed-positioned so it never disappears. */}
        <div className="sticky top-0 z-30 -mx-4 px-4 py-2.5 mb-2 bg-[#0a0510]/95 backdrop-blur-md border-b border-pink-500/20 flex items-center justify-end gap-2 shadow-lg pl-24">
          <div className="flex items-center gap-2">
            <Button
              onClick={shareRoom}
              variant="outline"
              size="sm"
              className="text-pink-200 border-pink-500/40 hover:bg-pink-500/20"
              data-testid="live-room-share-btn"
            >
              📤 Share
            </Button>
            {me && (
              <Button
                onClick={() => setShowCommunityShare(true)}
                variant="outline"
                size="sm"
                className="text-purple-200 border-purple-500/40 hover:bg-purple-500/20"
                data-testid="live-room-share-community-btn"
              >
                📣 To Community
              </Button>
            )}
            {/* 🎤 Guest "Leave Stage" — visible when current user is in the
                speakers list AND not the host. Clears speaker slot but
                keeps them in the room as a listener. */}
            {!isHost && me && (room?.guests || []).some((g) => g.user_id === me.user_id) && (
              <Button
                onClick={leaveStage}
                variant="outline"
                size="sm"
                className="text-amber-200 border-amber-500/40 hover:bg-amber-500/20"
                data-testid="live-room-leave-stage-btn"
              >
                🎤 Leave Stage
              </Button>
            )}
            {/* 🚪 Listener "Leave Beam" — visible to any non-host. Navigates
                out of the room; presence WS auto-cleans on unmount. */}
            {!isHost && (
              <Button
                onClick={leaveBeam}
                variant="outline"
                size="sm"
                className="text-pink-200 border-pink-500/40 hover:bg-pink-500/20"
                data-testid="live-room-leave-beam-btn"
              >
                🚪 Leave Beam
              </Button>
            )}
            {isHost && isLive && (
              <Button
                onClick={endRoom}
                variant="outline"
                size="sm"
                className="text-rose-200 border-rose-500/40 hover:bg-rose-500/20"
                data-testid="live-room-end-btn"
              >
                End Room
              </Button>
            )}
          </div>
        </div>

        {/* Host-leave confirmation prompt */}
        {showHostLeavePrompt && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            data-testid="host-leave-prompt"
            onClick={() => setShowHostLeavePrompt(false)}
          >
            <Card
              className="p-5 sm:p-6 max-w-sm w-full bg-[#1a1020] border-pink-500/40 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-black text-white mb-1">Leave room or end live?</h3>
              <p className="text-pink-200/70 text-sm mb-4">
                You're hosting a live beam. Leaving keeps the room running for your guests &
                listeners. Ending will close it for everyone.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={async () => {
                    setShowHostLeavePrompt(false);
                    await endRoom(true);
                  }}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold"
                  data-testid="host-leave-end-btn"
                >
                  🛑 End live for everyone
                </Button>
                <Button
                  onClick={() => {
                    // iter_181 (P0) — host chose to leave WITHOUT ending
                    // the room. Mark intentional so their LK disconnect
                    // doesn't count as a stability failure.
                    try { lkMarkIntentional && lkMarkIntentional("intentional"); } catch (_e) { /* ignore */ }
                    setShowHostLeavePrompt(false);
                    navigate("/home");
                  }}
                  variant="outline"
                  className="w-full text-pink-200 border-pink-500/40"
                  data-testid="host-leave-go-btn"
                >
                  🚪 Leave & keep it running
                </Button>
                <button
                  onClick={() => setShowHostLeavePrompt(false)}
                  className="text-pink-200/60 hover:text-pink-200 text-xs mt-1"
                  data-testid="host-leave-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* 🎬 Phase 9.7 Batch A — recording chip + host toggle */}
        <LiveRecordingControls
          recording={room.recording}
          isHost={isHost && isLive && recAvailable}
          busy={recBusy}
          onStart={startRecording}
          onStop={stopRecording}
        />

        {/* Phase 3 V1 — Private A&R Listening Room panel. Rendered only
            for rooms explicitly created with room_type="private_ar".
            Public/Beam rooms are unaffected. */}
        {room?.room_type === "private_ar" && (
          <PrivateARPanel
            roomId={roomId}
            me={me}
            isHost={isHost}
            arEvent={arEvent}
          />
        )}

        {/* Phase 21.1a — Edit Live modal (host only). Updates broadcast
            instantly to every viewer via the room_updated socket event. */}
        {showEditLive && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            data-testid="edit-live-modal"
            onClick={() => !editSaving && !editUploading && setShowEditLive(false)}
          >
            <Card
              className="p-5 sm:p-6 max-w-md w-full bg-[#1a1020] border-pink-500/40 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-black text-white">Edit Live</h3>
                <button
                  type="button"
                  onClick={() => setShowEditLive(false)}
                  disabled={editSaving || editUploading}
                  className="text-pink-200/60 hover:text-pink-200 text-lg disabled:opacity-30"
                  data-testid="edit-live-close-btn"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <p className="text-pink-200/70 text-xs mb-4">
                Updates appear instantly for every viewer — no refresh needed.
              </p>

              {/* Cover preview */}
              <label className="block text-[10px] text-pink-200/80 font-bold mb-1 uppercase tracking-wider">
                Cover Image
              </label>
              <div
                className="relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-white/5 border border-pink-500/30 mb-2 flex items-center justify-center"
                data-testid="edit-live-cover-preview"
              >
                {editCover ? (
                  <img
                    src={editCover}
                    alt="Live cover"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-pink-200/40 text-xs">No cover yet</span>
                )}
                {editUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-pink-100 text-sm font-bold">
                    Uploading…
                  </div>
                )}
              </div>
              <div className="flex gap-2 mb-4">
                <label className="flex-1 cursor-pointer text-center text-[11px] font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-400 hover:to-purple-500 transition-colors">
                  📷 Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={editUploading || editSaving}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadCoverFile(f);
                      e.target.value = "";
                    }}
                    data-testid="edit-live-cover-input"
                  />
                </label>
                {editCover && (
                  <button
                    type="button"
                    onClick={() => setEditCover("")}
                    disabled={editUploading || editSaving}
                    className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-white/10 text-pink-200 border border-white/20 hover:bg-white/20"
                    data-testid="edit-live-cover-clear-btn"
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Title */}
              <label className="block text-[10px] text-pink-200/80 font-bold mb-1 uppercase tracking-wider">
                Live Title
              </label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={80}
                placeholder="Beats are dropping…"
                disabled={editSaving}
                data-testid="edit-live-title-input"
                className="mb-1"
              />
              <p className="text-[10px] text-pink-200/50 mb-4 text-right">
                {(editTitle || "").length}/80
              </p>

              <div className="flex gap-2">
                <Button
                  onClick={saveLiveEdits}
                  disabled={editSaving || editUploading || !editTitle.trim()}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold disabled:opacity-50"
                  data-testid="edit-live-save-btn"
                >
                  {editSaving ? "Saving…" : "✨ Save & Broadcast"}
                </Button>
                <Button
                  onClick={() => setShowEditLive(false)}
                  disabled={editSaving || editUploading}
                  variant="outline"
                  className="text-pink-200 border-pink-500/40"
                  data-testid="edit-live-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Title + carrier banner — tighter padding so the speaker grid surfaces sooner */}
        <Card className="p-3 sm:p-4 mb-3 bg-gradient-to-br from-rose-500/10 to-pink-500/10 border-pink-500/30 relative overflow-hidden">
          {/* Phase 21.1a — Live Cover image (when set). Sits behind the
              header content as a soft backdrop so the title stays readable.
              Updates instantly via the room_updated socket event. */}
          {room.cover_image && (
            <img
              src={optimizeImage(room.cover_image, 'cover')}
              alt={room.title}
              data-testid="live-cover-image"
              className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
          <div className="relative flex items-start gap-3">
            <img
              src={getProfilePhotoUrl({
                profile_photo: room.host_profile_photo,
                picture: room.host_profile_photo,
              })}
              alt={room.host_name}
              className="w-14 h-14 rounded-full object-cover border-2 border-pink-500/60"
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black truncate" data-testid="live-room-title">{room.title}</h1>
                {isLive ? (
                  <span className="text-[9px] bg-rose-500 text-white px-1.5 rounded-full font-bold animate-pulse">LIVE</span>
                ) : (
                  <span className="text-[9px] bg-gray-500 text-white px-1.5 rounded-full font-bold">ENDED</span>
                )}
                {isHost && isLive && (
                  <button
                    type="button"
                    onClick={openEditLive}
                    data-testid="live-room-edit-btn"
                    className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-pink-100 border border-white/20 hover:bg-white/20 transition-colors"
                    title="Edit live title & cover"
                  >
                    ✏️ Edit
                  </button>
                )}
                {isHost && isLive && beamMusicEnabled && (
                  <button
                    type="button"
                    onClick={() => setMusicDrawerOpen(true)}
                    data-testid="beam-music-open-btn"
                    className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-fuchsia-500/30 text-fuchsia-100 border border-fuchsia-400/40 hover:bg-fuchsia-500/50 transition-colors"
                    title="Play music in your Beam"
                  >
                    🎵 Music
                  </button>
                )}
              </div>
              <p className="text-pink-200/80 text-xs">
                Hosted by <Link to={`/artist/${room.host_user_id}`} className="text-white font-semibold hover:underline">{room.host_name || "Anonymous"}</Link>
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="live-room-identity">
                <BeamKindBadge kind={room.beam_kind} />
                <BeamRoleBadge role={isHost ? "host" : meIsSpeaker ? "speaker" : me ? "listener" : "guest"} />
              </div>
              {room.topic && <p className="text-purple-200/80 text-sm mt-1">“{room.topic}”</p>}
              <div className="flex flex-wrap gap-3 text-[11px] mt-2">
                <span className="text-emerald-200" data-testid="live-room-online-count">🟢 {room.online_count || 0} {room.beam_kind === "audio" ? "listening" : "in the room"}</span>
                <span className="text-amber-200">🪙 {(room.gift_total_coins || 0).toLocaleString()} gifted</span>
                {wallet && <span className="text-purple-200">Wallet: {(wallet.balance || 0).toLocaleString()}c</span>}
              </div>
            </div>
          </div>
          {/* M2 — Tap to Hear Music overlay. iOS Safari and recent
              Android Chrome block audio.play() without a user gesture;
              when _applyMusicStateToAudio catches the rejection it
              flips `audioBlocked`. One tap resumes the audio element
              in-place at the authoritative server position. */}
          {beamMusicEnabled && audioBlocked && beamMusicState?.track_id && (
            <div
              data-testid="beam-music-autoplay-overlay"
              className="mx-4 mt-2 p-3 rounded-xl bg-amber-500/15 border border-amber-400/40 flex items-center gap-3"
            >
              <div className="flex-1 text-amber-100 text-xs">
                <div className="font-bold">🎧 Tap to hear the room music</div>
                <div className="opacity-80">Your browser needs one tap to start audio.</div>
              </div>
              <button
                type="button"
                onClick={tapToHearMusic}
                data-testid="beam-music-tap-to-hear"
                className="px-3 py-1.5 rounded-full bg-amber-400 text-black text-xs font-bold"
              >
                ▶ Play music
              </button>
            </div>
          )}
          {/* Beam Music Drawer (M1) — host-only. Renders nothing when
              closed; owns a short-lived HTMLAudioElement separate from
              the global PlayerProvider so the two never contend. Also
              gated by backend-advertised BEAM_MUSIC_ENABLED flag (Option
              A) so the feature stays hidden in production until the
              Music Player is CEO-approved for public release. */}
          {isHost && beamMusicEnabled && (
            <BeamMusicDrawer
              roomId={roomId}
              isOpen={musicDrawerOpen}
              onClose={() => setMusicDrawerOpen(false)}
              musicState={beamMusicState}
              onPlay={playBeamMusic}
              onPauseResume={pauseResumeBeamMusic}
              onClear={clearBeamMusic}
              pending={beamMusicPending}
              duckingEnabled={duckingEnabled}
              onToggleDucking={() => setDuckingEnabled((v) => !v)}
            />
          )}
          {/* W0/W1 (Feb 2026) — AI Empire Workshop Presentation Mode.
              Mounted ONLY when the server-authoritative is_workshop flag
              is true (set at event-create time when an rgm_workshop
              event links to this room). Normal Beam rooms get an exact
              zero-render here. Host-only controls live inside the
              component and are gated on `canHost`. */}
          {room?.is_workshop && (
            <div className="mt-3" data-testid="workshop-mode-wrapper">
              <PresentationStage
                roomId={roomId}
                presentationState={room.presentation_state}
                canHost={isHost}
                roomTitle={room.title}
              />
            </div>
          )}
          <p className="relative mt-3 text-[11px] text-amber-200/80" data-testid="live-room-audio-banner">
            {!livekitConfigured ? (
              <>🎧 <span className="font-semibold">Voice & Video carrier offline.</span> Chat, presence, gifts, and quests still work.</>
            ) : lkState === "connected" ? (
              <>{room.beam_kind === "audio" ? "🎧" : "📹"} <span className="font-semibold">{room.beam_kind === "audio" ? "Audio Beam · voice only." : "Live video & audio."}</span> {lkRole === "listener" ? (me ? "You're listening. Raise your hand to ask the host to bring you on stage." : "You're listening as a guest. Sign in free to chat, raise your hand, follow or send gifts.") : (room.beam_kind === "audio" ? "Your mic is live when it's on." : "You can publish camera & microphone.")}
                {lkAudioBlocked && (
                  <button type="button" onClick={lkStartAudio} className="ml-2 px-3 py-1 rounded-full bg-amber-400 text-black font-bold" data-testid="live-room-tap-to-hear">🔊 Tap to hear the room</button>
                )}</>
            ) : lkState === "connecting" ? (
              <>📹 <span className="font-semibold">Connecting to the room…</span> {lkAttempts > 1 && <span className="text-amber-200/60">(attempt {lkAttempts})</span>}</>
            ) : lkState === "reconnecting" ? (
              <>📹 <span className="font-semibold animate-pulse">Reconnecting…</span> Hang tight.</>
            ) : lkState === "failed" ? (
              <span data-testid="live-room-media-failed">
                ⚠️ <span className="font-semibold">Couldn't connect to the room's {room.beam_kind === "audio" ? "audio" : "video and audio"}.</span>{" "}
                {lkFailedReason && (
                  <span className="text-rose-200/70 mr-1" data-testid="live-room-media-failed-reason">
                    ({lkFailedReason})
                  </span>
                )}
                Chat &amp; gifts still work.{" "}
                <button
                  type="button"
                  onClick={lkRetry}
                  data-testid="live-room-media-retry-btn"
                  className="ml-1 underline font-bold text-amber-100 hover:text-white"
                >
                  Try again
                </button>
              </span>
            ) : (
              <>📹 <span className="font-semibold">Getting the room ready…</span> If this takes more than a few seconds, check your connection and refresh.</>
            )}
          </p>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* LEFT: speaker grid + chat */}
          <div className="md:col-span-2 space-y-3">
            <Card className="p-3 bg-white/5 border-white/10 relative" data-testid="live-room-speakers">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-pink-200/70 font-bold">
                  🪑 Beam Box · {beamBoxSpeakers.length}/{beamCapacity} speaker{beamBoxSpeakers.length === 1 ? "" : "s"}
                  {/* Phase B — Lock / Unlock Boxes control for host or
                      room-scoped monitor. Hidden from ordinary
                      participants/listeners. */}
                  <span className="ml-2 inline-block align-middle">
                    <BeamBoxLockToggle
                      canModerate={canModerate}
                      boxesLocked={boxesLocked}
                      isLive={isLive}
                      onToggle={toggleBoxesLock}
                    />
                  </span>
                </p>
                {/* Real-time reactions — click to broadcast to everyone */}
                {isLive && me && (
                  <div className="flex gap-1" data-testid="live-room-reactions">
                    {["🔥", "❤️", "🎉", "👏", "😂"].map((e) => (
                      <button
                        key={e}
                        onClick={() => sendReaction(e)}
                        className="text-base p-1 hover:scale-125 transition-transform"
                        title="Send reaction"
                        data-testid={`live-room-reaction-${e}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Floating reaction layer */}
              <div className="absolute inset-x-0 top-12 pointer-events-none flex justify-center gap-2 z-10">
                {reactionFloaters.map((f) => (
                  <span
                    key={f.id}
                    className="text-3xl animate-bounce"
                    style={{ animationDuration: "1.6s" }}
                    data-testid="live-room-reaction-floater"
                  >
                    {f.emoji}
                  </span>
                ))}
              </div>
              {/* 📹 Camera + Mic controls — host & invited guests only */}
              {lkCanPublish && (
                <div
                  className="flex flex-wrap items-center gap-2 mb-2 pb-2 border-b border-white/5"
                  data-testid="livekit-media-controls"
                >
                  {room.beam_kind !== "audio" && <button
                    onClick={lkToggleCamera}
                    className={`text-[11px] px-2.5 py-1 rounded-full font-bold transition-colors ${
                      lkCameraOn
                        ? "bg-emerald-500/30 text-emerald-100 border border-emerald-400/50"
                        : "bg-white/5 text-pink-200/80 border border-white/10 hover:bg-white/10"
                    }`}
                    data-testid="livekit-toggle-camera"
                  >
                    {lkCameraOn ? "📹 Camera On" : "📷 Camera Off"}
                  </button>}
                  <button
                    onClick={lkToggleMic}
                    className={`text-[11px] px-2.5 py-1 rounded-full font-bold transition-colors ${
                      lkMicOn
                        ? "bg-emerald-500/30 text-emerald-100 border border-emerald-400/50"
                        : "bg-white/5 text-pink-200/80 border border-white/10 hover:bg-white/10"
                    }`}
                    data-testid="livekit-toggle-mic"
                  >
                    {lkMicOn ? "🎙 Mic On" : "🔇 Mic Off"}
                  </button>
                  {lkCameraOn && (
                    <button
                      onClick={switchCamera}
                      className="text-[11px] px-2.5 py-1 rounded-full font-bold bg-white/5 text-pink-200/80 border border-white/10 hover:bg-white/10 transition-colors"
                      title="Flip front/back camera"
                      data-testid="livekit-switch-camera"
                    >
                      🔄 Flip
                    </button>
                  )}
                  {(lkCameraDenied || lkMicDenied) && (
                    <span
                      className="text-[10px] text-rose-200/90 bg-rose-500/15 border border-rose-400/30 rounded-full px-2 py-0.5"
                      data-testid="livekit-permission-denied"
                    >
                      {lkCameraDenied && lkMicDenied
                        ? "Camera & mic blocked — your avatar is shown instead."
                        : lkCameraDenied
                        ? "Camera blocked — avatar shown."
                        : "Mic blocked — others can't hear you."}
                    </span>
                  )}
                </div>
              )}
              {/* Phase 21.0 — Dynamic Beam Box grid. Columns scale with the
                  active capacity tier so 5/8/12/16/20 each have a clean
                  responsive layout. Real speakers fill from index 0; the
                  remaining slots render as "Open" placeholders so artists
                  can SEE the room growing as people join. */}
              <div
                className={`grid gap-3 transition-all duration-500 ease-out ${gridClassForCapacity(beamCapacity)}`}
                data-testid={`beam-box-grid-${beamCapacity}`}
                data-beam-capacity={beamCapacity}
                data-beam-speakers={beamBoxSpeakers.length}
              >
                {beamBoxSpeakers.map((u) => {
                  const isMuted = mutedSet.has(u.user_id);
                  const isPinned = room.pinned_guest_id === u.user_id;
                  const lkParticipant = lkParticipantByIdentity.get(u.user_id);
                  // Phase 21.3a — Hybrid State Synchronization gate.
                  // A tile renders as an ACTIVE speaker only when BOTH:
                  //   (a) backend has approved them (they're in `guests`)
                  //   (b) LiveKit reports their participant connected
                  // For the host, condition (b) is implicit — the host
                  // always controls the room even before publishing.
                  // The local viewer also bypasses the gate so they see
                  // themselves immediately.
                  const isMe = me?.user_id === u.user_id;
                  const isJoining = !u.is_host && !isMe && !lkParticipant;
                  // 📣 Visual prominence: pinned > host > guest. Pinned tiles
                  // get a thicker amber glow; host gets pink, guests get
                  // purple — so the audience can read the room at a glance.
                  // Phase 9.6 Batch 3 — recent-gift spotlight glow overrides
                  // the default ring for ~3.5s after a gift lands.
                  const isGifted = !!glowByUserId[u.user_id];
                  const tileRing = isGifted
                    ? "ring-4 ring-amber-300 shadow-[0_0_30px_8px_rgba(245,158,11,0.6)] animate-pulse"
                    : isPinned
                    ? "ring-2 ring-amber-400/80 shadow-lg shadow-amber-500/30"
                    : u.is_host
                    ? "ring-2 ring-pink-400/60 shadow-md shadow-pink-500/20"
                    : isJoining
                    ? "ring-2 ring-amber-300/60 animate-pulse"
                    : "ring-1 ring-purple-400/40 hover:ring-purple-300/70 transition-shadow";
                  return (
                    <div
                      key={u.user_id}
                      className={`relative rounded-xl flex flex-col gap-1 ${tileRing}`}
                      data-testid={`beam-box-speaker-${u.user_id}`}
                      data-joining={isJoining ? "true" : "false"}
                    >
                      <button
                        type="button"
                        onClick={() => setPreviewUserId(u.user_id)}
                        className="w-full block cursor-pointer text-left"
                        data-testid={`speaker-avatar-btn-${u.user_id}`}
                        aria-label={`Preview ${u.name || "speaker"} profile`}
                      >
                        <LiveKitVideoTile
                          participant={lkParticipant}
                          fallbackUser={u}
                          isHost={!!u.is_host}
                          isPinned={isPinned}
                          isMuted={isMuted}
                        />
                      </button>
                      {/* Phase 21.3a — "Joining…" overlay until LiveKit
                          confirms the participant. Prevents the
                          "approved but invisible" / "visible before
                          approval" UX bugs. */}
                      {isJoining && (
                        <div
                          className="absolute inset-0 rounded-xl bg-black/55 backdrop-blur-[1px] flex items-center justify-center pointer-events-none"
                          data-testid={`beam-box-joining-${u.user_id}`}
                        >
                          <span className="text-[11px] font-bold text-amber-200 bg-black/60 px-2 py-1 rounded-full border border-amber-400/40">
                            ⏳ Joining…
                          </span>
                        </div>
                      )}
                      {/* Role label badge — Phase B FINAL (L4):
                          HOST / GUEST stay as the existing absolute
                          bottom-left pill (per CEO: Host badge
                          behaviour preserved). MONITOR is removed
                          from the absolute overlay and rendered
                          below in normal flow so it sits directly
                          beneath the artist name without covering
                          avatar/controls. */}
                      {(() => {
                        const isMon = (room?.monitor_user_ids || []).includes(u.user_id);
                        if (isMon && !u.is_host) {
                          return null;
                        }
                        const label = u.is_host ? "🎙️ Host" : "🎤 Speaker";
                        const cls = u.is_host
                          ? "bg-gradient-to-r from-pink-500 to-purple-600"
                          : "bg-gradient-to-r from-purple-500 to-indigo-600";
                        return (
                          <span
                            className={u.is_host ? "sr-only" : `absolute top-2 left-2 z-10 pointer-events-none text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white ${cls}`}
                            data-testid={`speaker-role-${u.user_id}`}
                          >
                            {label}
                          </span>
                        );
                      })()}
                      {/* Phase B FINAL (L4) — MONITOR badge in normal
                          flow directly below the artist name. Not
                          absolute, so it never covers the avatar,
                          controls, or the Joining overlay. Long
                          names still render cleanly because the
                          wrapper is flex-col with min-w-0 truncation. */}
                      {(room?.monitor_user_ids || []).includes(u.user_id) && !u.is_host && (
                        <span
                          className="mx-auto mt-0.5 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white bg-gradient-to-r from-cyan-500 to-sky-600"
                          data-testid={`speaker-monitor-badge-${u.user_id}`}
                        >
                          🎛 Monitor
                        </span>
                      )}
                      {/* Host controls — never visible to non-hosts */}
                      {isHost && !u.is_host && (
                        <div className="relative z-20 flex gap-1 justify-center mt-1" data-testid={`beam-box-host-controls-${u.user_id}`}>
                          <button
                            onClick={() => toggleMute(u.user_id, isMuted)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 hover:bg-white/20"
                            title={isMuted ? "Unmute" : "Mute"}
                            data-testid={`beam-box-mute-${u.user_id}`}
                          >
                            {isMuted ? "🔊" : "🔇"}
                          </button>
                          <button
                            onClick={() => togglePin(u.user_id, isPinned)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 hover:bg-white/20"
                            title={isPinned ? "Unpin" : "Pin"}
                            data-testid={`beam-box-pin-${u.user_id}`}
                          >
                            {isPinned ? "📍" : "📌"}
                          </button>
                          <button
                            onClick={() => removeGuest(u.user_id)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 hover:bg-rose-500/30"
                            title="Remove from Beam Box"
                            data-testid={`beam-box-remove-${u.user_id}`}
                          >
                            ✕
                          </button>
                          <button
                            onClick={() => blockUser(u.user_id)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-rose-600/30 hover:bg-rose-600/50"
                            title="Block from room"
                            data-testid={`beam-box-block-${u.user_id}`}
                          >
                            🚫
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Phase 21.0 — Lazy-rendered "Open" placeholder slots fill
                    the rest of the active capacity tier. Pure decoration —
                    no media subscriptions or participant connections, so
                    rendering 15 extra placeholders costs nothing. */}
                {Array.from({ length: Math.max(0, beamCapacity - beamBoxSpeakers.length) }).map((_, i) => (
                  <BeamBoxEmptySlot
                    key={`beam-slot-${i}`}
                    slotIndex={i}
                    boxesLocked={boxesLocked}
                    isHost={isHost}
                    meIsSpeaker={meIsSpeaker}
                    meIsBanned={meIsBanned}
                    stageFull={stageFull}
                    isLive={isLive}
                    isAuthed={!!me}
                    onJoin={joinBox}
                    isJoiningBox={joiningBox}
                  />
                ))}
              </div>
              {/* Phase 21.0 — Room Expanded banner. Shows briefly after every
                  tier unlock (5→8→12→16→20). Pure CSS animation, no layout
                  shift on the speakers grid above. */}
              {showExpandedBanner && (
                <div
                  data-testid="beam-room-expanded-banner"
                  className="mt-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500/30 to-purple-500/30 border border-emerald-400/50 text-emerald-100 text-sm font-bold flex items-center gap-2 animate-in slide-in-from-top duration-300"
                >
                  <span className="text-lg">🎉</span>
                  <span>Room Expanded — More artists can now join! ({beamCapacity} slots)</span>
                </div>
              )}
              {/* Phase 21.0 — "Room Full · You're next in line" for listeners
                  whose hand is up. Surfaces only when capacity is saturated. */}
              {beamBoxSpeakers.length >= beamCapacity && handQueue.some((q) => q?.user_id === me?.user_id) && (
                <div
                  data-testid="beam-room-full-queue-notice"
                  className="mt-3 px-4 py-2.5 rounded-xl bg-amber-500/15 border border-amber-400/40 text-amber-100 text-sm font-bold flex items-center gap-2"
                >
                  <span className="text-lg">🪑</span>
                  <span>
                    Room Full — You&apos;re next in line ({handQueue.findIndex((q) => q?.user_id === me?.user_id) + 1}
                    {" of "}{handQueue.length}). The next open slot is yours.
                  </span>
                </div>
              )}
              {/* Listeners + host invite controls */}
              {(promotableListeners.length > 0 || isHost) && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  {/* 🙋 Host / Monitor hand queue panel — F3 (Feb 2026):
                      canModerate (isHost || isMonitor) can see + approve.
                      Deny stays host-only (gated at onClick below). */}
                  {canModerate && handQueue.length > 0 && (
                    <div
                      className="mb-3 p-2 rounded-lg bg-amber-500/15 border border-amber-400/40"
                      data-testid="hand-queue-panel"
                    >
                      <p className="text-[10px] uppercase tracking-wider text-amber-200 font-bold mb-1.5 flex items-center gap-1">
                        🔔 Raise Hand Queue
                        <span className="bg-amber-500 text-black rounded-full px-1.5 text-[10px] animate-pulse">
                          {handQueue.length}
                        </span>
                      </p>
                      {isHost && (
                        <label className="flex items-center gap-1.5 text-[10px] text-amber-100/80 mb-1.5 cursor-pointer select-none" data-testid="hand-auto-admit-row">
                          <input type="checkbox" checked={autoAdmit} onChange={(e) => setAutoAdmit(e.target.checked)} className="accent-amber-400" data-testid="hand-auto-admit-toggle" />
                          Auto-admit the next hand when a stage spot opens
                        </label>
                      )}
                      <div className="flex flex-col gap-1.5">
                        {handQueue.map((h) => (
                          <div
                            key={h.user_id}
                            className="flex items-center gap-2 bg-black/30 rounded-md px-2 py-1.5"
                            data-testid={`hand-queue-row-${h.user_id}`}
                          >
                            <button
                              onClick={() => setPreviewUserId(h.user_id)}
                              className="shrink-0"
                              data-testid={`hand-queue-avatar-${h.user_id}`}
                            >
                              <img
                                src={getProfilePhotoUrl(h)}
                                alt={h.name}
                                className="w-7 h-7 rounded-full object-cover border border-amber-400/50"
                                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
                              />
                            </button>
                            <span className="flex-1 text-xs text-white truncate">{h.name}</span>
                            <button
                              onClick={() => approveHand(h.user_id)}
                              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/30 hover:bg-emerald-500/50 text-emerald-100 border border-emerald-400/40"
                              data-testid={`hand-approve-${h.user_id}`}
                            >
                              ✓ Approve
                            </button>
                            {/* F3 (Feb 2026) — Deny stays host-only.
                                Monitors can approve a listener who
                                already asked, but not decline / silence
                                someone else's request. */}
                            {isHost && (
                              <button
                                onClick={() => denyHand(h.user_id)}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/30 hover:bg-rose-500/50 text-rose-100 border border-rose-400/40"
                                data-testid={`hand-deny-${h.user_id}`}
                              >
                                ✕ Deny
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 🙋 Listener Raise Hand button — only for non-host, non-speaker, signed-in users */}
                  {!me && isLive && (
                    <div className="mb-3 flex justify-center">
                      <button type="button" onClick={() => navigate(`/login?next=/beams/${roomId}`)} className="text-xs font-bold px-4 py-2 rounded-full border-2 border-white/20 bg-white/5 text-pink-100" data-testid="live-room-signin-to-raise">
                        🙋 Sign in free to raise your hand
                      </button>
                    </div>
                  )}
                  {!isHost && me && !beamBoxSpeakers.find((s) => s.user_id === me.user_id) && isLive && (
                    <div className="mb-3 flex justify-center">
                      <button
                        onClick={myHandRaised ? lowerHand : raiseHand}
                        className={`text-xs font-bold px-4 py-2 rounded-full border-2 transition-all ${
                          myHandRaised
                            ? "bg-amber-500/20 text-amber-200 border-amber-400/60 hover:bg-amber-500/30"
                            : "bg-gradient-to-r from-pink-500 to-purple-600 text-white border-white/20 hover:from-pink-400 hover:to-purple-500 shadow-lg shadow-pink-500/20"
                        }`}
                        data-testid={myHandRaised ? "live-room-lower-hand-btn" : "live-room-raise-hand-btn"}
                      >
                        {myHandRaised ? "🙋 Lower Hand" : "🙋 Request to Come Up"}
                      </button>
                    </div>
                  )}

                  <p className="text-[10px] uppercase tracking-wider text-pink-200/70 font-bold mb-2">
                    Also in the room ({promotableListeners.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {promotableListeners.length === 0 && (
                      <p className="text-pink-200/50 text-[11px]">Waiting for listeners…</p>
                    )}
                    {promotableListeners.slice(0, 12).map((u) => {
                      const handUp = !!handQueue.find((q) => q.user_id === u.user_id);
                      return (
                        <div
                          key={u.user_id}
                          className="flex flex-col items-center gap-1 w-14"
                          data-testid={`live-room-listener-${u.user_id}`}
                        >
                          <button
                            type="button"
                            onClick={() => setPreviewUserId(u.user_id)}
                            className="relative cursor-pointer"
                            data-testid={`listener-avatar-btn-${u.user_id}`}
                            aria-label={`Preview ${u.name || "listener"} profile`}
                          >
                            <img
                              src={getProfilePhotoUrl(u)}
                              alt={u.name}
                              className="w-10 h-10 rounded-full object-cover hover:ring-2 hover:ring-pink-400/60 transition-all"
                              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
                            />
                            {handUp && (
                              <span
                                className="absolute -top-1 -right-1 text-sm bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center shadow"
                                title="Hand raised"
                              >
                                🙋
                              </span>
                            )}
                            {/* Phase 9.7.5 — visible Monitor pill on listener avatars */}
                            {(room?.monitor_user_ids || []).includes(u.user_id) && (
                              <span
                                className="absolute -bottom-1 -right-1 text-[8px] font-black uppercase tracking-wider px-1 py-[1px] rounded-full bg-cyan-500 text-white shadow"
                                title="Monitor"
                                data-testid={`listener-monitor-badge-${u.user_id}`}
                              >
                                🎛
                              </span>
                            )}
                          </button>
                          <span className="text-[10px] text-white truncate w-full text-center">{u.name}</span>
                          {isHost && (
                            <button
                              onClick={() => promoteListener(u.user_id)}
                              disabled={(room.guests?.length || 0) >= (BEAM_MAX_CAPACITY - 1)}
                              className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/30 hover:bg-rose-500/50 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                              title={(room.guests?.length || 0) >= (BEAM_MAX_CAPACITY - 1) ? "Beam Box full (20 max)" : "Invite to Beam Box"}
                              data-testid={`live-room-invite-${u.user_id}`}
                            >
                              ↑ Beam
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>

            {/* Chat */}
            <Card className="p-3 bg-white/5 border-white/10 flex flex-col h-[360px]" data-testid="live-room-chat">
              <p className="text-[10px] uppercase tracking-wider text-pink-200/70 font-bold mb-2">Chat</p>
              <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                {chat.length === 0 && (
                  <p className="text-pink-200/50 text-xs text-center py-6">
                    Say hi 👋
                  </p>
                )}
                {chat.map((m) => (
                  <div key={m.id} className="flex items-start gap-2 text-sm" data-testid={`live-room-chat-msg-${m.id}`}>
                    <button
                      type="button"
                      onClick={() => m.from?.user_id && setPreviewUserId(m.from.user_id)}
                      className="shrink-0 mt-0.5"
                      data-testid={`chat-avatar-btn-${m.from?.user_id || "anon"}`}
                      aria-label={`Preview ${m.from?.name || "user"} profile`}
                    >
                      <img
                        src={getProfilePhotoUrl({
                          profile_photo: m.from?.profile_photo,
                          picture: m.from?.profile_photo,
                        })}
                        alt={m.from?.name}
                        className="w-6 h-6 rounded-full object-cover hover:ring-2 hover:ring-pink-400/60 transition-all"
                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
                      />
                    </button>
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => m.from?.user_id && setPreviewUserId(m.from.user_id)}
                        className="text-[11px] text-pink-200/80 font-semibold hover:text-pink-100"
                      >
                        {m.from?.name}
                      </button>
                      <p className="text-white text-sm break-words">{m.text}</p>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder={me ? "Say something…" : "Sign in free to chat"}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendChat()}
                  disabled={!me || !isLive}
                  data-testid="live-room-chat-input"
                />
                <Button onClick={sendChat} disabled={!me || !isLive} data-testid="live-room-chat-send">
                  Send
                </Button>
              </div>
            </Card>

            {/* Phase 21.1b — Inline gift Card removed. Gift Drawer
                (bottom-sheet) is rendered at the page root so it never
                blocks the Beam grid or live video. */}
          </div>

          {/* RIGHT: live gift feed + quests + leaderboard */}
          <div className="space-y-3">
            {/* Phase 9.7.6 — Live Earnings Panel (always visible).
                Phase 14.3 — pulses + floats "+coins" on each gift. */}
            <LiveEarningsPanel
              room={room}
              leaderboard={leaderboard}
              recentGiftCount={recentGifts.length}
              pulse={earningsPulse}
              isHost={isHost}
              hostWalletBalance={isHost ? wallet?.balance : null}
            />
            {/* Phase 9.7.5 — Beam Safety Rules (always visible, expandable) */}
            <BeamSafetyRules canModerate={isHost || (room?.monitor_user_ids || []).includes(me?.user_id)} />
            <QuestsPanel enabled={!!me} refreshTick={questsRefreshTick} />
            <Card className="p-3 bg-white/5 border-white/10 max-h-[300px] flex flex-col" data-testid="live-room-gift-feed">
              <p className="text-[10px] uppercase tracking-wider text-amber-200/80 font-bold mb-2">
                🎁 Live Gifts
              </p>
              <div className="flex-1 overflow-y-auto space-y-1.5">
                {recentGifts.length === 0 ? (
                  <p className="text-pink-200/50 text-xs text-center py-6">First gift sets the tone…</p>
                ) : (
                  // Phase 21.1g+ — sort by timestamp DESC for newest-first;
                  // stable compound key so duplicate gift_ids from re-broadcasts
                  // never collide.
                  [...recentGifts]
                    .sort((a, b) => {
                      const ta = a.created_at ? Date.parse(a.created_at) : 0;
                      const tb = b.created_at ? Date.parse(b.created_at) : 0;
                      return tb - ta;
                    })
                    .map((g) => {
                      const key = g.transaction_id
                        || `${g.sender?.user_id || "anon"}_${g.gift_id || "?"}_${g.created_at || Math.random()}`;
                      return (
                    <div key={key} className="flex items-center gap-2 p-1.5 rounded-md bg-amber-500/10 border border-amber-500/20" data-testid="live-room-gift-feed-item">
                      <span className="text-xl">{g.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">
                          <span className="font-semibold">{g.sender?.artist_name || "Listener"}</span>{" "}
                          sent{" "}
                          {g.quantity > 1 && (
                            <span className="text-amber-200 font-black">{g.quantity}× </span>
                          )}
                          <span className="text-amber-200 font-bold">{g.name}</span>
                        </p>
                        <p className="text-[10px] text-amber-200/70">+{g.coins.toLocaleString()}c</p>
                      </div>
                    </div>
                      );
                    })
                )}
              </div>
            </Card>

            <Card className="p-3 bg-white/5 border-white/10" data-testid="live-room-leaderboard">
              <p className="text-[10px] uppercase tracking-wider text-amber-200/80 font-bold mb-2">
                🏆 Top Supporters in this Room
              </p>
              {leaderboard.length === 0 ? (
                <p className="text-pink-200/50 text-xs text-center py-3">No gifts yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {leaderboard.slice(0, 8).map((s) => (
                    <Link
                      key={s.user_id}
                      to={`/artist/${s.user_id}`}
                      className="flex items-center gap-2 p-1.5 rounded-md hover:bg-white/5 transition-colors"
                      data-testid={`live-room-lb-${s.user_id}`}
                    >
                      <span className="text-xs text-pink-200/70 w-5 text-right">#{s.rank}</span>
                      <img
                        src={getProfilePhotoUrl(s)}
                        alt={s.artist_name}
                        className="w-7 h-7 rounded-full object-cover"
                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
                      />
                      <span className="flex-1 text-white text-sm truncate">{s.artist_name}</span>
                      <span className="text-amber-200 text-xs font-bold">{s.total_coins.toLocaleString()}c</span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* 👤 Profile preview modal — opened from any avatar tap */}
      {previewUserId && (
        <ProfilePreviewModal
          userId={previewUserId}
          viewerId={me?.user_id}
          onClose={() => setPreviewUserId(null)}
          isMonitor={(room?.monitor_user_ids || []).includes(previewUserId)}
          monitorAction={isHost ? async (target, makeMonitor) => {
            const path = makeMonitor ? "promote" : "demote";
            try {
              const rr = await api.post(`/live/rooms/${roomId}/monitors/${path}`,
                { user_id: target.user_id });
              setRoom((prev) => prev ? { ...prev,
                monitor_user_ids: rr.data?.monitor_user_ids || prev.monitor_user_ids,
              } : prev);
              toast.success(makeMonitor
                ? `🎛️ @${target.artist_name || target.name || "user"} is now a Monitor`
                : `Removed monitor role from @${target.artist_name || target.name || "user"}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't update monitor");
            }
          } : null}
          onSendGift={(target) => {
            // Phase 21.1b — Tap avatar → mini profile → Send Gift now opens
            // the GiftDrawer with the recipient pre-selected.
            setGiftTargetId(target.user_id);
            setGiftDrawerOpen(true);
            toast(`🎁 Choose a gift for @${target.artist_name || target.name || "them"}`);
          }}
        />
      )}

      {/* 🎁 Live gift overlay — floating toasts, combo, takeover */}
      <LiveGiftOverlay ref={giftOverlayRef} />

      {/* Phase 21.1b — 🎁 Gift Drawer FAB (bottom-sheet trigger). Hidden
          when drawer is open. Always positioned bottom-right so it never
          collides with the chat input or the Beam grid. */}
      {isLive && me && giftRecipientCandidates.length > 0 && !giftDrawerOpen && (
        <button
          type="button"
          onClick={() => setGiftDrawerOpen(true)}
          data-testid="gift-drawer-trigger"
          className="fixed bottom-4 right-4 z-[55] px-4 py-3 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-white font-black shadow-2xl shadow-amber-500/40 border-2 border-white/20 hover:from-amber-400 hover:to-pink-400 active:scale-95 transition-all flex items-center gap-2"
          aria-label="Open gift drawer"
        >
          <span className="text-xl">🎁</span>
          <span className="text-sm">Gift</span>
        </button>
      )}

      <GiftDrawer
        open={giftDrawerOpen && isLive}
        onClose={() => setGiftDrawerOpen(false)}
        catalog={giftCatalog}
        wallet={wallet}
        recipientCandidates={giftRecipientCandidates}
        giftTargetId={giftTargetId}
        setGiftTargetId={setGiftTargetId}
        effectiveTarget={effectiveGiftTarget}
        sending={sending}
        onSend={(g, q) => sendGift(g, effectiveGiftTarget, q)}
      />

      {/* 🎬 Phase 9.7 Batch A — post-end Replay Save / Discard modal */}
      {showReplayModal && (
        <ReplayPostEndModal
          roomId={roomId}
          onClose={() => { setShowReplayModal(false); navigate("/home"); }}
        />
      )}

      {/* Phase 21.4 Batch 5 — 📣 Share this live to a Crew / Group / Club. */}
      <ShareToCommunityDialog
        open={showCommunityShare}
        onClose={() => setShowCommunityShare(false)}
        liveShare={{
          roomId,
          title: room?.title || "Live on RGM",
          url: `${window.location.origin}/beams/${roomId}`,
          cover: room?.cover_image || null,
          hostName: room?.host_name || null,
        }}
      />
    </div>
  );
};

export default LiveRoomPage;
